<template>
  <FloatingWindow
      :window-id="'layer-window-' + mapName"
      title="レイヤー"
      :show-title-on-mobile="false"
      :default-width="380"
      :default-height="650"
      :default-top="65"
      :default-left="mapName === 'map02' ? undefined : 10"
      :default-right="mapName === 'map02' ? 60 : undefined"
      :anchor="mapName === 'map02' ? 'right' : 'left'"
      :custom-icons="filteredCustomIcons"
      :is-modal="isSmallScreen"
      :use-fixed-position="true"
      :delayed-fade-in="true"
      :fade-in-delay="100"
      @undo="handleUndo"
      @redo="handleRedo"
      @save-startup-layers="handleSaveStartupLayers"
      @restore-initial="handleRestoreInitial"
      @reset-layers="resetLayers"
      @close="handleClose"
      @width-changed="checkLabelOverflow"
  >
    <div :id="'container-div-' + mapName" :style="menuContentSize" class="container-div">
      <div :id="'first-div-' + mapName" class="first-div">

        <!-- ドロー行（地物がある場合のみ表示、常に最上部） -->
        <div v-if="hasDrawFeatures" class="drag-item draw-layer-item">
          <div class="handle-div" style="visibility: hidden;">
            <v-icon class="handle-icon" size="20">mdi-drag</v-icon>
          </div>

          <div class="visible-layer-div">
            <v-icon @click="toggleDrawVisibility" class="clickable-icon" size="20">{{ s_drawVisible ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
          </div>

          <div class="info-div" style="visibility: hidden;">
            <v-icon>mdi-information</v-icon>
          </div>

          <div class="delete-div" @click="deleteAllDraw">
            <v-icon>mdi-delete</v-icon>
          </div>

          <div class="label-div draw-label">ドロー</div>
          <div class="range-div">
            <input type="range" min="0" max="1" step="0.01" class="range" v-model.number="s_drawOpacity" @input="onDrawOpacityInput" @mouseover="changeWatchFlg(false)" @mouseleave="changeWatchFlg(true)" @touchstart.stop @touchmove.stop/>
          </div>
        </div>

        <draggable @start="onStart" @end="onEnd" v-model="s_selectedLayers[mapName]" item-key="id" handle=".handle-div">
          <template #item="{element}">
            <div class="drag-item">

              <div class="handle-div">
                <v-icon class="handle-icon hover" size="20">mdi-drag</v-icon>
              </div>

              <div class="visible-layer-div">
                <v-icon @click="toggleCheck(element)" class="clickable-icon" size="20">{{ element.visibility ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
              </div>

              <div class="info-div" @click="infoOpen(element, true, true)">
                <v-icon>{{ element.ext ? 'mdi-cog' : 'mdi-information' }}</v-icon>
              </div>

              <div class="delete-div" v-track="'layer-dialog-delete'" @click="removeLayer(element.id)">
                <v-icon>mdi-delete</v-icon>
              </div>

              <v-tooltip v-if="layerMemos[element.id]" location="bottom" :max-width="300" :open-delay="300">
                <template v-slot:activator="{ props }">
                  <div class="label-div" :ref="'label-' + element.id" v-html="element.label" v-bind="props" />
                </template>
                <span style="white-space: pre-wrap">{{ layerMemos[element.id].memo }}</span>
              </v-tooltip>
              <div v-else class="label-div" :ref="'label-' + element.id" v-html="element.label" />
              <!-- 右側アイコン群（ジャンプ＋メモ＋ケバブ） -->
              <div class="layer-right-icons">
                <v-icon
                  v-if="jumpLocations[element.id]"
                  class="layer-right-icon jump-icon"
                  size="18"
                  @click.stop="flyToJumpLocation(element.id)"
                >mdi-map-marker-radius</v-icon>
                <v-icon
                  v-if="layerMemos[element.id]"
                  class="layer-right-icon memo-icon"
                  size="18"
                  @click.stop="onMemoIconClick(element)"
                >mdi-note-text</v-icon>
                <v-menu v-if="$store.state.isTeacher || isGeneralUser" location="bottom end">
                  <template v-slot:activator="{ props }">
                    <v-icon
                      v-bind="props"
                      class="layer-right-icon kebab-icon"
                      size="18"
                      @click.stop
                    >mdi-dots-vertical</v-icon>
                  </template>
                  <v-list density="compact" class="kebab-menu-list">
                    <v-list-item prepend-icon="mdi-map-marker-radius" @click="onKebabClick(element)">
                      <v-list-item-title>ジャンプ設定</v-list-item-title>
                    </v-list-item>
                    <v-list-item prepend-icon="mdi-pencil-box-outline" @click="onMemoClick(element)">
                      <v-list-item-title>メモ</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <div class="range-div">
                <input type="range" min="0" max="1" step="0.01" class="range" v-model.number="element.opacity" @input="throttledInputSlider(element)" @change="changeSlider" @mouseover="changeWatchFlg(false)" @mouseleave="changeWatchFlg(true)" @touchstart.stop @touchmove.stop/>
              </div>
            </div>
          </template>
        </draggable>
      </div>

      <div :id="'center-div-' + mapName" class="center-div"></div>

      <div :id="'second-div-' + mapName" :style="secondDivFlexStyle" class="second-div">
        <div class="search-header">
          <div class="d-flex align-center ga-2 w-100">
            <v-text-field
                label="地図抽出"
                v-model="searchText"
                prepend-inner-icon="mdi-map-search"
                :style="isSearchFocused ? 'flex: 1;' : 'width: 120px;'"
                variant="outlined"
                density="compact"
                hide-details
                @keydown="handleSearchKeydown"
                @focus="isSearchFocused = true"
                @blur="isSearchFocused = false"
            />
            <!-- AI検索ボタン（GQLチャットに統合したため無効化）
            <MiniTooltip v-show="!isSearchFocused" text="AIで検索" :offset-x="0" :offset-y="4">
              <v-btn
                  v-track="'layer-ai-search'"
                  size="small"
                  color="grey"
                  variant="tonal"
                  @click="openAiSearchDialog"
                  class="layer-action-btn"
              >
                <v-icon size="large">mdi-creation</v-icon>
              </v-btn>
            </MiniTooltip>
            -->
            <MiniTooltip v-show="!isSearchFocused" text="ブックマークのみ表示" :offset-x="0" :offset-y="4">
              <v-btn
                  size="small"
                  :color="showOnlyFavorites ? 'amber' : 'grey'"
                  :variant="showOnlyFavorites ? 'flat' : 'tonal'"
                  @click="toggleFavoritesFilter"
                  class="layer-action-btn"
              >
                <v-icon size="large">{{ showOnlyFavorites ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
              </v-btn>
            </MiniTooltip>
            <!-- マイセットボタン: チップ行に統合したためコメントアウト
            <v-btn
              v-show="!isSearchFocused"
              v-track="'popular-sets-open'"
              size="small"
              color="primary"
              variant="tonal"
              prepend-icon="mdi-star-box-multiple"
              style="position: relative !important; top: -2px !important; height: 40px !important;"
              @click="openPopularSets"
            >
              マイセット
            </v-btn>
            -->
            <MiniTooltip v-show="!isSearchFocused" text="陰影レイヤーを表示" :offset-x="0" :offset-y="4">
              <v-btn
                  size="small"
                  color="primary"
                  :variant="hillshade.maps[mapName]  ? 'flat' : 'tonal'"
                  :aria-pressed="hillshade.maps[mapName]  ? 'true' : 'false'"
                  @click="hsEnabledMapsToggle"
                  class="layer-action-btn"
              >
                <v-icon size="large">mdi-terrain</v-icon>
              </v-btn>
            </MiniTooltip>
          </div>
        </div>

        <!-- マイセット クイック切替チップ（ユーザーセットのみ） -->
        <div v-if="!isSearchFocused && !searchText && (userId || mysetUserSets.length)" class="myset-chips">
          <MiniTooltip v-if="userId" text="マイセット保存" :offset-x="0" :offset-y="4">
            <v-btn
              icon
              :size="22"
              variant="flat"
              color="primary"
              class="myset-chip-add"
              @click="quickCreateSet"
            >
              <v-icon size="small">mdi-plus</v-icon>
            </v-btn>
          </MiniTooltip>
          <draggable
            v-model="mysetChips"
            item-key="id"
            class="myset-chips-scroll"
            :animation="200"
            :delay="200"
            :delay-on-touch-only="true"
            :swap-threshold="0.5"
            @end="onMysetDragEnd"
          >
            <template #item="{element: set}">
              <v-chip
                size="small"
                :color="isActiveSet(set) ? 'primary' : undefined"
                :variant="isActiveSet(set) ? 'flat' : 'outlined'"
                class="myset-chip"
                @click="quickApplySet(set)"
                @contextmenu.prevent="openChipMenu($event, set)"
              >
                {{ set.name }}
              </v-chip>
            </template>
          </draggable>
        </div>

        <div class="tree-container-wrapper">
          <!-- 最近使用したレイヤー（シンプルなDIV実装） -->
          <div v-if="recentLayersForDisplay.length > 0 && !searchText && !showOnlyFavorites" class="recent-layers-section">
            <!-- ヘッダー（クリックで開閉） -->
            <div class="recent-layers-header" @click="trackRecentLayersToggle">
              <div class="recent-layers-icon-wrapper">
                <svg
                  v-if="!recentLayersExpanded"
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
              <span class="recent-layers-title">最近使用したレイヤー</span>
            </div>

            <!-- スクロール可能なリスト -->
            <div
              v-if="recentLayersExpanded"
              class="recent-layers-list-wrapper"
              :style="{ height: recentLayersHeight + 'px', maxHeight: recentLayersHeight + 'px' }"
            >
              <div class="recent-layers-list">
                <div
                  v-for="layer in cachedRecentLayers"
                  :key="layer.id"
                  class="recent-layer-row"
                  :class="{ 'layer-click-highlight': layer.id === layerClickHighlightId }"
                  @click="onNodeClick(layer)"
                >
                  <div class="recent-layer-item">
                    <!-- お気に入り星は表示しない（通常ツリーで操作する） -->
                    <div v-html="layer.label" class="tree-node-label"></div>
                    <v-icon
                      class="recent-pin-icon"
                      :class="{ 'is-pinned': isRecentLayerPinned(layer.id) }"
                      @click.stop="toggleRecentLayerPin(layer.id)"
                      size="small"
                    >
                      {{ isRecentLayerPinned(layer.id) ? 'mdi-bookmark' : 'mdi-bookmark-outline' }}
                    </v-icon>
                    <v-icon
                      class="recent-delete-icon"
                      @click.stop="removeRecentLayer(layer.id)"
                      size="small"
                    >
                      mdi-delete
                    </v-icon>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="recentLayersExpanded"
              class="recent-layers-resize-handle"
              @mousedown.prevent="startRecentLayersResize"
              @touchstart.prevent="startRecentLayersResize"
            ></div>
          </div>

          <!-- 通常のレイヤーツリー -->
          <div class="tree-scroll-container scrollable-content" ref="treeScrollContainer">
            <!-- お気に入りモード時：ドラッグ可能なリスト -->
            <draggable
                v-if="showOnlyFavorites"
                v-model="sortedFavorites"
                item-key="id"
                class="favorite-draggable-list"
                @end="onFavoriteDragEnd"
                :animation="200"
                handle=".favorite-drag-handle"
            >
              <template #item="{ element }">
                <div class="favorite-item-row" :class="{ 'layer-click-highlight': element.id === layerClickHighlightId }">
                  <div class="favorite-item">
                    <v-icon class="favorite-drag-handle" size="small">mdi-drag</v-icon>
                    <div class="favorite-item-label" v-html="element.label" @click="onNodeClick(element)"></div>
                    <v-icon
                      class="favorite-icon is-favorite"
                      @click.stop="toggleFavorite(element)"
                      size="small"
                    >
                      mdi-star
                    </v-icon>
                  </div>
                </div>
              </template>
            </draggable>

            <!-- 通常モード時：ツリー表示 -->
            <Tree
                v-else
                :nodes="filteredLayers"
                :search-text="searchText"
                :use-checkbox="false"
                :use-icon="true"
                @nodeClick="onNodeClick"
                @nodeExpanded="onNodeExpanded"
            >
              <!-- カスタム描画 -->
              <template #tree-node="{ node }">
                <div class="tree-node-with-favorite">
                  <span v-if="isDiorama3DLayer(node.id)" class="badge-3d" title="3Dジオラマ対応">3D</span>
                  <span v-if="showLessonSyncBadge(node)" class="badge-sync" title="授業モードで先生の操作が生徒に同期されます">
                    <v-icon size="10">mdi-broadcast</v-icon>
                  </span>
                  <div v-html="node.label" class="tree-node-label"></div>
                  <v-icon
                    v-if="node.id && !node.children && !node.nodes"
                    class="favorite-icon"
                    :class="{ 'is-favorite': isFavorite(node.id) }"
                    @click.stop="toggleFavorite(node)"
                    size="small"
                  >
                    {{ isFavorite(node.id) ? 'mdi-star' : 'mdi-star-outline' }}
                  </v-icon>
                </div>
              </template>
            </Tree>

            <!-- オンボーディング吹き出し -->
            <v-overlay
                v-if="welcomeWizard.currentStep === 2 && welcomeWizard.open"
                :model-value="true"
                contained
                persistent
                scrim="transparent"
                class="onboarding-overlay"
            >
              <v-card
                  class="onboarding-tooltip"
                  elevation="8"
                  max-width="320"
              >
                <v-card-text class="pa-4">
                  <div class="d-flex align-center mb-2">
                    <v-icon color="primary" size="32" class="mr-2">mdi-hand-pointing-right</v-icon>
                    <h3 class="text-h6 font-weight-bold">レイヤーをクリック！</h3>
                  </div>
                  <p class="text-body-2 mb-3">
                    左側のレイヤーリストから好きなレイヤーをクリックすると、地図上に表示されます。
                  </p>
                  <p class="text-body-2 mb-0">
                    <strong>おすすめ：</strong> 「標準地図」「航空写真」「色別標高図」などを試してみましょう！
                  </p>
                </v-card-text>
              </v-card>
            </v-overlay>
          </div>
        </div>
      </div>
    </div>
  </FloatingWindow>

  <!-- AI検索ウィンドウ -->
  <FloatingWindow
      :window-id="'ai-layer-search-window'"
      title="AIでレイヤー検索"
      :default-width="350"
      :default-height="400"
      :default-top="65"
      :default-left="400"
      :is-modal="isSmallScreen"
      :use-fixed-position="true"
      :delayed-fade-in="true"
      :fade-in-delay="100"
      overflow="hidden"
      @close="closeAiSearchDialog"
  >
    <div class="ai-search-content">
      <!-- 固定部分：検索入力とボタン -->
      <div class="ai-search-fixed pa-3">
        <v-text-field
          ref="aiSearchInput"
          v-model="aiSearchQuery"
          label="どんな地図を探していますか？"
          placeholder="例: 洪水のハザードマップ、古い航空写真"
          variant="outlined"
          density="comfortable"
          hide-details
          :disabled="aiSearching"
          :loading="aiSearching"
          @keydown.enter="handleAiSearchEnter"
          @compositionstart="aiSearchComposing = true"
          @compositionend="aiSearchComposing = false"
        />
        <div class="d-flex align-center mt-3 mb-2">
          <p class="text-caption text-grey flex-grow-1">
            自然言語で地図レイヤーを検索できます
          </p>
          <v-btn
            v-track="'layer-ai-search-execute'"
            color="primary"
            size="small"
            variant="elevated"
            :loading="aiSearching"
            :disabled="!aiSearchQuery.trim()"
            @click="executeAiSearch"
          >
            検索
          </v-btn>
        </div>
      </div>

      <!-- スクロール部分：検索結果 -->
      <div v-if="aiSearchResults.length > 0" class="ai-search-scroll-area">
        <div class="px-3 pb-1">
          <div class="d-flex align-center mb-2">
            <span class="text-caption text-grey">検索結果</span>
            <v-chip size="x-small" color="primary" class="ml-2">{{ aiSearchResultLayers.length }}件</v-chip>
          </div>
          <div v-if="aiSearchReason" class="text-caption text-grey-darken-1 mb-2">
            {{ aiSearchReason }}
          </div>
        </div>
        <div ref="aiSearchResultsRef" class="ai-search-results mx-3 mb-3">
          <div
            v-for="layer in aiSearchResultLayers"
            :key="layer.id"
            class="ai-result-item"
            @click="selectAiResult(layer)"
          >
            <v-icon size="default" color="primary" class="mr-2">mdi-layers</v-icon>
            <span v-html="layer.label"></span>
          </div>
        </div>
      </div>

      <div v-if="aiSearchResults.length === 0 && aiSearchReason" class="px-3 pb-3">
        <v-alert type="info" density="compact" variant="tonal">
          該当するレイヤーが見つかりませんでした
        </v-alert>
      </div>
    </div>
  </FloatingWindow>

  <!-- 教員用ジャンプ位置設定FW（レイヤーごとに1つ） -->
  <Teleport to="body">
    <FloatingWindow
      v-for="jd in jumpDialogs"
      :key="'jump-' + jd.layerId"
      :window-id="'jump-dialog-' + mapName + '-' + jd.layerId"
      title="ジャンプ位置設定"
      :default-width="320"
      :default-top="jd.top"
      :default-left="jd.left"
      :use-fixed-position="true"
      @close="closeJumpDialog(jd.layerId)"
    >
      <div class="jump-dialog-content">
        <div class="jump-dialog-layer-name" v-html="jd.label" />
        <div class="d-flex ga-2 mb-2">
          <v-text-field label="緯度" :model-value="jd.lat" variant="outlined" density="compact" hide-details readonly />
          <v-text-field label="経度" :model-value="jd.lng" variant="outlined" density="compact" hide-details readonly />
          <v-text-field label="ズーム" :model-value="jd.zoom" variant="outlined" density="compact" hide-details readonly style="max-width: 80px" />
        </div>
        <div class="text-caption text-grey mb-3">地図を動かすと自動更新されます</div>
        <div class="d-flex ga-2">
          <v-btn v-if="jumpLocations[jd.layerId]" color="error" variant="text" @click="deleteJumpLocationFor(jd)">削除</v-btn>
          <v-spacer />
          <v-btn color="primary" @click="saveJumpLocationFor(jd)">保存</v-btn>
        </div>
      </div>
    </FloatingWindow>
  </Teleport>

  <!-- メモ用FloatingWindow（レイヤーごとに1つ） -->
  <Teleport to="body">
    <FloatingWindow
      v-for="md in memoDialogs"
      :key="'memo-' + md.layerId"
      :window-id="'memo-dialog-' + mapName + '-' + md.layerId"
      :title="md.readOnly ? 'メモ' : 'メモ編集'"
      :default-width="320"
      :default-top="md.top"
      :default-left="md.left"
      :use-fixed-position="true"
      @close="closeMemoDialog(md.layerId)"
    >
      <div class="memo-dialog-content">
        <div class="memo-dialog-layer-name" v-html="md.label" />
        <template v-if="md.readOnly">
          <div class="memo-dialog-text">{{ md.memoText }}</div>
        </template>
        <template v-else>
          <v-textarea
            v-model="md.memoText"
            variant="outlined"
            density="compact"
            hide-details
            rows="4"
            auto-grow
            placeholder="このレイヤーについてメモを入力..."
          />
          <div class="d-flex ga-2 mt-2">
            <v-btn v-if="layerMemos[md.layerId]" color="error" variant="text" @click="deleteMemoFor(md)">削除</v-btn>
            <v-spacer />
            <v-btn color="primary" @click="saveMemoFor(md)">保存</v-btn>
          </div>
        </template>
      </div>
    </FloatingWindow>
  </Teleport>

  <!-- マイセット チップ右クリックメニュー -->
  <v-menu
    v-model="chipMenu.show"
    :target="chipMenu.target"
    location="bottom"
    :close-on-content-click="true"
  >
    <v-list density="compact" nav>
      <v-list-item prepend-icon="mdi-pencil" title="名称変更" @click="openChipRename" />
      <v-list-item prepend-icon="mdi-delete" title="削除" class="text-error" @click="deleteChipSet" />
    </v-list>
  </v-menu>

  <!-- マイセット名称変更ダイアログ -->
  <v-dialog v-model="chipRenameDialog" max-width="400">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-pencil</v-icon>
        セット名を変更
      </v-card-title>
      <v-card-text>
        <v-text-field
          v-model="chipRenameValue"
          label="セット名"
          variant="outlined"
          density="compact"
          autofocus
          @compositionstart="mysetComposing = true"
          @compositionend="mysetComposing = false"
          @keydown.enter="!mysetComposing && saveChipRename()"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="chipRenameDialog = false">キャンセル</v-btn>
        <v-btn color="primary" variant="flat" :disabled="!chipRenameValue" @click="saveChipRename">変更</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- マイセット新規作成ダイアログ -->
  <v-dialog v-model="mysetCreateDialog" max-width="400">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-layers-plus</v-icon>
        マイセットに保存
      </v-card-title>
      <v-card-text>
        <div class="text-body-2 mb-3" style="color: var(--oh3-color-text-sub);">
          現在のレイヤー構成に名前を付けて保存します。次回からワンタップで同じレイヤーを呼び出せます。
        </div>
        <v-text-field
          v-model="mysetNewName"
          label="セット名（例：現地調査用、防災確認）"
          variant="outlined"
          density="compact"
          autofocus
          @compositionstart="mysetComposing = true"
          @compositionend="mysetComposing = false"
          @keydown.enter="!mysetComposing && saveQuickSet()"
        />
        <div class="text-caption mt-1" style="color: var(--oh3-color-text-sub);">
          <v-icon size="x-small" class="mr-1">mdi-layers</v-icon>
          {{ ($store.state.selectedLayers?.[mapName] || []).length }}個のレイヤーが保存されます
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="mysetCreateDialog = false">キャンセル</v-btn>
        <v-btn color="primary" variant="flat" :disabled="!mysetNewName" @click="saveQuickSet">保存</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

import { generateSegmentLabelGeoJSON, generateStartEndPointsFromGeoJSON, deleteAll, geojsonUpdate } from "@/js/pyramid";
import { updatePolygonEdgeLabels } from "@/js/draw";
import throttle from 'lodash/throttle'
import { showError, showLoginPrompt } from '@/js/utils/messageHelper'
import { isExtSyncCapable } from '@/js/utils/lessonSyncHelper'
import { setLayerOpacity, setLayerVisibility, applyOpacityAndVisibility, addLayerWithFadeIn } from '@/js/layerEngine/mapLayerOps'
import { computeLayerSnapshot, diffLayerSnapshots } from '@/js/layerEngine/diffEngine'
import { applyHighlights } from '@/js/layerEngine/postProcess'
import { restoreUserFiles, restoreUploadedAssets } from '@/js/layerEngine/initRestore'
import { requestAddLayers, consumeAddLayersRequest } from '@/js/layerEngine/addLayersGate'
import { setPointCloudBeforeId } from '@/js/pointcloud/deckOverlayManager'
import { phpApiPost, phpApiGet } from '@/js/phpApi'
// aistCopcManager は maplibre-gl-lidar(633KB) + @deck.gl(300KB+) を含むため遅延ロード
// import { setCopcBeforeId } from '@/js/pointcloud/aistCopcManager'

const registeredLayers = new Set()

const FULLSCREEN_DIALOG_WIDTH_THRESHOLD = 450

import {
  highlightSpecificFeatures,
  highlightSpecificFeatures2025,
  highlightSpecificFeaturesCity,
  LngLatToAddress,
  highlightSpecificFeaturesSima,
  highlightSpecificFeaturesDxf,
  getNextZIndex,
  forseMoveLayer, fitClickCircleAll, readdByIdPreservingLast,
  startUrl,
  vConfirm,
} from "@/js/downLoad";
import originalStyle from '@/assets/json/osm_bright_label_only.json'
import * as Layers from '@/js/layers'
import Tree from '@/components/custom-tree/Tree.vue'
import '@/components/custom-tree/tree-style.css'
import draggable from "vuedraggable"
import FloatingWindow from '@/components/floatingwindow/FloatingWindow.vue'
import * as turf from '@turf/turf'
import {
  arrowsEndpointLabelLayer,
  arrowsEndpointLayer,
  clickCircleKeikoLineLayer,
  clickCircleLabelLayer,
  clickCircleCenterLabelLayer,
  clickCircleMemoMarkLayer,
  clickCircleLayer, clickCircleLayerEx,
  clickCircleLineLayer,
  clickCircleLineDashedLayer,
  clickCircleLineDottedLayer,
  clickCirclePolygonLineLayer,
  clickCircleSource, clickCircPolygonSymbolAreaLayer, clickCircPolygonPopulationLayer,
  clickCircPolygonSymbolLayer,
  clickCircSymbolLayer,
  clickPointLayer,
  clickPointSource, dragHandleslayer, dragHandlesSource,
  drawSelectionSource, drawSelectionPolygonFillLayer, drawSelectionPolygonLineLayer,
  drawSelectionLineLayer, drawSelectionPointLayer,
  elevationLayer, elevationSource,
  endPointSouce, freehandPreviewLayer, freehandPreviewSource,
  guideLineLayer,
  guideLineSource, guideLineVertexLayer,
  midpointLayer,
  midpointSource, segmentLabeleLayer, segmentLabelSource,
  polygonEdgeLabelSource, polygonEdgeLabelLayer,
  vertexLayer,
  vertexSource,
  zenkokuChibanzuAddLayer
} from "@/js/layers";
import store from "@/store";
import {mapState} from "vuex";
import axios from "axios"
import MiniTooltip from "@/components/MiniTooltip.vue";
import { getUsageTracker } from '@/js/utils/usageTracker'
import Fuse from 'fuse.js'


// マップごとに独立したダイアログずらしカウンター
let infoCount = {
  map01: 0,
  map02: 0,
  map03: 0,
  map04: 0,
}

export default {
  name: 'Dialog-layer',
  props: ['mapName'],
  components: {
    MiniTooltip,
    Tree,
    draggable,
    FloatingWindow
  },
  data: () => ({
    flyZoom: 14.5,
    counter: 0,     // 0=init(地番図), 1=restore(アップロード復元), 2+=ready(diff有効)
    enginePhase: 'init',  // 'init' | 'restoring' | 'ready' — counterの意味を明示
    nodeClicked: false,
    isDragging:false,
    isChecked: true,
    searchText: '',
    showOnlyFavorites: false,
    isSearchFocused: false,
    selectedLayers: {
      map01:[],
      map02:[]
    },
    layers:[],
    addLayersScheduleId: null,
    addLayersRetryCount: 0,
    // 起動直後は permalink/sessionState からのレイヤー復元が走り、addSource/addLayer が
    // 地図初期化と競合して失敗することがある。最初の数秒間はスナックバーを抑止して
    // 「復元中の一時的な失敗」をユーザーに見せない。コンソール warn は残す。
    layerErrorSuppressUntil: Date.now() + 5000,
    waitingForMap: false,
    waitingForStyle: false,
    prevSnapshot: {},
    prevFullLayers: {},
    jumpLocations: {},
    jumpDialogs: [],
    jumpLoadTimer: null,
    layerMemos: {},
    memoDialogs: [],
    memoLoadTimer: null,
    menuContentSize: {'padding': '10px', 'user-select': 'text', 'box-sizing': 'border-box'},
    customIcons: [
      {
        icon: 'mdi-content-save',
        eventName: 'save-startup-layers',
        tooltip: '起動時レイヤー設定',
      },
      {
        icon: 'mdi-restore',
        eventName: 'restore-initial',
        tooltip: '設定値にリセット'
      },
      {
        icon: 'mdi-delete-sweep',
        eventName: 'reset-layers',
        tooltip: ''
      }
    ],
    layerHistory: [],
    historyIndex: -1,
    maxHistorySize: 50,
    usageTracker: null,
    recentLayersExpanded: false, // 最近使用レイヤーの展開状態
    cachedRecentLayers: [], // 表示中の最近使用レイヤーのキャッシュ
    layerClickHighlightId: null,
    layerClickHighlightTimer: null,
    recentLayersHeight: 200,
    isResizingRecentLayers: false,
    recentLayersResizeStartY: 0,
    recentLayersResizeStartHeight: 200,
    pinnedRecentLayerIds: [],
    // AI検索関連
    aiSearching: false, // AI検索中フラグ
    aiSearchResults: [], // AI検索結果のレイヤーIDリスト
    aiSearchReason: '', // AI検索の理由
    aiSearchLayerMap: {}, // AI検索用レイヤーマップ（id -> {id, label}）
    showAiResults: false, // AI検索結果表示フラグ
    aiSearchQuery: '', // AI検索クエリ
    aiSearchComposing: false, // IME変換中フラグ
    // マイセット クイック切替
    mysetUserSets: [],
    mysetChips: [], // draggable用の表示配列
    mysetCreateDialog: false,
    mysetNewName: '',
    // チップ右クリックメニュー
    chipMenu: { show: false, target: null, set: null },
    chipRenameDialog: false,
    chipRenameValue: '',
    mysetComposing: false,
  }),
  computed: {
    ...mapState([
      'hillshade',
      'userId',
      'isSmall500',
      'recentLayerIds',
      'welcomeWizard',
      'is4ScreenMode',
      'userSetsVersion',
    ]),
    isSmallScreen() {
      return this.isSmall500
    },
    // レイヤーFWの表示状態（スロット初回描画の検知用）
    isLayerWindowVisible() {
      return !!this.$store.state.floatingWindows?.['layer-window-' + this.mapName]
    },
    // ドローの地物があるかチェック（config以外）
    hasDrawFeatures() {
      // map01のみ対応（ドローはmap01固定）
      if (this.mapName !== 'map01') return false
      const geojsonText = this.$store.state.clickCircleGeojsonText
      if (!geojsonText) return false
      try {
        const data = JSON.parse(geojsonText)
        if (!data || !data.features) return false
        return data.features.some(f => f.properties?.id !== 'config')
      } catch {
        return false
      }
    },
    // ドロー透過度
    s_drawOpacity: {
      get() {
        return this.$store.state.drawOpacity
      },
      set(value) {
        this.$store.state.drawOpacity = value
      }
    },
    // ドロー表示/非表示
    s_drawVisible: {
      get() {
        return this.$store.state.drawVisible
      },
      set(value) {
        this.$store.state.drawVisible = value
      }
    },
    s_hillshadEnabled: {
      get() {
        return this.$store.state.hillshadEnabled
      },
      set(value) {
        this.$store.state.hillshadEnabled = value
      }
    },
    s_secondDivStyle: {
      get() {
        return this.$store.state.secondDivStyle
      },
      set(value) {
        this.$store.state.secondDivStyle = value
      }
    },
    // flexbox用のスタイル（初期heightを除外）
    secondDivFlexStyle() {
      const { height, ...rest } = this.$store.state.secondDivStyle
      return rest
    },
    // 一般ユーザー判定（教員でも生徒でもないログインユーザー）
    isGeneralUser() {
      return !!this.$store.state.userId && !this.$store.state.isTeacher && !this.$store.state.isStudent
    },
    // ジャンプ位置取得用のteacher_id（教員→自分、生徒→担当教員、一般→null）
    effectiveTeacherId() {
      if (this.$store.state.isTeacher && this.$store.state.teacherInfo) {
        return this.$store.state.teacherInfo.id
      }
      const classes = this.$store.state.studentClasses
      if (classes && classes.length > 0) {
        return classes[0].teacher_id
      }
      return null
    },
    // マイセット: システムセット定義
    mysetSystemSets() {
      return [
        { id: 'land-registry', name: '登記調査', _system: true, layers: ['oh-homusyo-2025-layer','oh-homusyo-2025-kijyunten','oh-seamlessphoto','oh-pale-layer'] },
        { id: 'lot-number-survey', name: '地番調査', _system: true, layers: ['oh-chibanzu-all2','oh-seamlessphoto','oh-pale-layer'] },
        { id: 'aerial-timeline', name: '航空写真時系列', _system: true, layers: ['oh-seamlessphoto','oh-sp87','oh-sp74','oh-sp61','oh-sp45'] },
        { id: 'hazard-map', name: 'ハザードマップ', _system: true, layers: ['oh-densho','oh-kozui-saidai','oh-tsunami','oh-tameike','oh-dosya','oh-dosekiryu','oh-kyukeisya','oh-jisuberi','oh-pale-layer'] },
      ]
    },
    s_lngRange () {
      return this.$store.state.lngRange
    },
    s_latRange () {
      return this.$store.state.latRange
    },
    s_dialogsINfo () {
      return this.$store.state.dialogsInfo
    },
    s_dialogs () {
      return this.$store.state.dialogs.layerDialog
    },
    s_selectedLayers: {
      get() {
        return this.$store.state.selectedLayers
      },
      set(value) {
        this.$store.state.selectedLayers = value
      }
    },
    filteredCustomIcons() {
      if (!this.userId) {
        return this.customIcons.filter(icon =>
          icon.eventName !== 'restore-initial' &&
          icon.eventName !== 'save-startup-layers'
        )
      }
      return this.customIcons
    },
    // お気に入りレイヤーIDのリスト
    favoriteLayerIds() {
      return this.$store.state.favoriteLayerIds || []
    },
    // 現在のユーザーID（ログイン状態変更監視用）
    currentUserId() {
      return localStorage.getItem('lastUserId') || 'guest'
    },
    // 最近使用レイヤーの表示用データ
    recentLayersForDisplay() {
      if (!this.recentLayerIds || this.recentLayerIds.length === 0) {
        return []
      }

      const findLayerById = (nodes, targetId) => {
        if (!nodes || !Array.isArray(nodes)) return null
        for (const node of nodes) {
          if (node.id === targetId) {
            return node // ディープコピーせず直接参照を返す
          }
          const children = node.children || node.nodes
          if (children && children.length > 0) {
            const found = findLayerById(children, targetId)
            if (found) return found
          }
        }
        return null
      }

      const recentLayers = []
      const pinnedLayers = []
      const pinnedSet = new Set(this.pinnedRecentLayerIds)

      for (const layerId of this.pinnedRecentLayerIds) {
        const layer = findLayerById(this.layers, layerId)
        if (layer && this.recentLayerIds.includes(layerId)) {
          pinnedLayers.push(layer)
        }
      }

      for (const layerId of this.recentLayerIds) {
        if (pinnedSet.has(layerId)) continue
        const layer = findLayerById(this.layers, layerId)
        if (layer) {
          recentLayers.push(layer)
        }
      }

      return [...pinnedLayers, ...recentLayers]
    },
    // お気に入りフィルター適用後のレイヤーツリー（最近使用は含まない）
    filteredLayers() {
      // お気に入りフィルター適用
      if (this.showOnlyFavorites) {
        return this.extractFavoriteLayers(this.layers)
      }

      // デフォルトはそのまま返す（最近使用は別Treeで表示）
      return this.layers
    },
    // お気に入りレイヤーをfavoriteLayerIdsの順序でソートしたもの
    sortedFavorites: {
      get() {
        const favorites = this.extractFavoriteLayers(this.layers)
        // favoriteLayerIdsの順序に従ってソート
        return favorites.sort((a, b) => {
          const indexA = this.favoriteLayerIds.indexOf(a.id)
          const indexB = this.favoriteLayerIds.indexOf(b.id)
          return indexA - indexB
        })
      },
      set(newOrder) {
        // ドラッグ後の新しい順序を保存
        const newFavoriteIds = newOrder.map(layer => layer.id)
        this.$store.dispatch('updateFavoriteLayerOrder', newFavoriteIds)
      }
    },
    // AI検索結果のレイヤーオブジェクトを取得
    aiSearchResultLayers() {
      if (!this.aiSearchResults || this.aiSearchResults.length === 0) {
        return []
      }
      // AIから返ったIDで部分一致検索
      const resultLayers = []
      const usedIds = new Set()
      for (const targetId of this.aiSearchResults) {
        // 完全一致を先に試す
        if (this.aiSearchLayerMap[targetId]) {
          if (!usedIds.has(targetId)) {
            usedIds.add(targetId)
            resultLayers.push(this.aiSearchLayerMap[targetId])
          }
          continue
        }
        // 部分一致: targetIdで始まるIDを探す
        for (const [id, layer] of Object.entries(this.aiSearchLayerMap)) {
          if (id.startsWith(targetId) && !usedIds.has(id)) {
            usedIds.add(id)
            resultLayers.push(layer)
          }
        }
      }
      return resultLayers
    },
  },
  methods: {
    // レイヤーFW内の上下ペイン分割ドラッグハンドルの初期化
    // FloatingWindowのスロットが遅延レンダリングのため、初回表示後に呼ぶ必要がある
    setupLayerSplitDrag() {
      if (this._layerSplitSetupDone) return
      const handleEl = document.getElementById('center-div-' + this.mapName)
      const firstDiv = document.getElementById('first-div-' + this.mapName)
      const secondDiv = document.getElementById('second-div-' + this.mapName)
      const container = document.getElementById('container-div-' + this.mapName)
      // スロット未描画ならまだ初期化できない（次の可視化時に再試行）
      if (!handleEl || !firstDiv || !secondDiv || !container) return
      this._layerSplitSetupDone = true

      // iOS バウンス防止
      let touchStartY = 0
      firstDiv.addEventListener('touchstart', (e) => {
        touchStartY = e.touches[0].pageY
      }, { passive: false })
      firstDiv.addEventListener('touchmove', (e) => {
        const currentY = e.touches[0].pageY
        const scrollTop = firstDiv.scrollTop
        const scrollHeight = firstDiv.scrollHeight
        const clientHeight = firstDiv.clientHeight
        const isAtTop = scrollTop === 0
        const isAtBottom = scrollTop + clientHeight >= scrollHeight
        if ((isAtTop && currentY > touchStartY) || (isAtBottom && currentY < touchStartY)) {
          e.preventDefault()
        }
      }, { passive: false })

      if (window.innerWidth <= 480) return

      // first-divの高さだけ固定し、second-divは flex:1 1 auto（CSS既定）のままにして、
      // FW自体のリサイズ時に下側が自動で追従するようにする。
      const storageKey = `oh3-layer-split-${this.mapName}`
      const savedSplit = localStorage.getItem(storageKey)
      if (savedSplit) {
        try {
          const parsed = JSON.parse(savedSplit)
          if (parsed && parsed.top) firstDiv.style.height = parsed.top
        } catch (error) {
          console.warn('Failed to restore layer split:', error)
        }
      }

      let isDragging = false
      const startDrag = () => { isDragging = true }
      const endDrag = () => {
        if (!isDragging) return
        isDragging = false
        const topHeight = `${Math.round(firstDiv.getBoundingClientRect().height)}px`
        try {
          localStorage.setItem(storageKey, JSON.stringify({ top: topHeight }))
        } catch (error) {
          console.warn('Failed to save layer split:', error)
        }
      }
      const dragMove = (clientY) => {
        if (!isDragging) return
        const containerRect = container.getBoundingClientRect()
        const topHeight = clientY - containerRect.top
        const bottomHeight = containerRect.height - topHeight - handleEl.offsetHeight
        if (topHeight > 0 && bottomHeight > 0) {
          firstDiv.style.height = `${topHeight - 8}px`
        }
      }

      handleEl.addEventListener('mousedown', startDrag)
      handleEl.addEventListener('touchstart', startDrag, { passive: false })
      document.addEventListener('mouseup', endDrag)
      document.addEventListener('mousemove', (e) => dragMove(e.clientY))
      document.addEventListener('touchend', endDrag, { passive: true })
      document.addEventListener('touchmove', (e) => {
        if (e.touches.length > 0) dragMove(e.touches[0].clientY)
      }, { passive: false })
    },
    // ドロー表示/非表示を切り替え
    toggleDrawVisibility() {
      this.s_drawVisible = !this.s_drawVisible
      this.applyDrawVisibility()
    },
    // ドロー表示状態を適用
    applyDrawVisibility() {
      const map = this.$store.state.map01
      if (!map) return
      const v = this.s_drawVisible ? 'visible' : 'none'
      const layerIds = [
        'click-circle-layer',
        'click-circle-polygon-line-layer',
        'click-circle-polygon-symbol-layer',
        'click-circle-polygon-symbol-area-layer',
        'click-circle-line-layer',
        'click-circle-keiko-line-layer',
        'click-circle-label-layer',
        'click-circle-center-label-layer',
        'click-circle-symbol-layer',
        'arrows-endpoint-layer',
        'segment-label-layer'
      ]
      layerIds.forEach(layerId => {
        if (map.getLayer(layerId)) {
          map.setLayoutProperty(layerId, 'visibility', v)
        }
      })
      this.configChangeDraw('visible', this.s_drawVisible)
    },
    // ドロー透過度入力時
    onDrawOpacityInput() {
      this.applyDrawOpacity()
      this.configChangeDraw('opacity', this.s_drawOpacity)
    },
    // ドロー透過度を適用
    applyDrawOpacity() {
      const map = this.$store.state.map01
      if (!map) return
      if (map.getLayer('click-circle-layer')) {
        map.setPaintProperty('click-circle-layer', 'fill-opacity', this.s_drawOpacity)
      }
      if (map.getLayer('click-circle-polygon-line-layer')) {
        map.setPaintProperty('click-circle-polygon-line-layer', 'line-opacity', this.s_drawOpacity)
      }
      if (map.getLayer('click-circle-polygon-symbol-layer')) {
        map.setPaintProperty('click-circle-polygon-symbol-layer', 'text-opacity', this.s_drawOpacity)
      }
      if (map.getLayer('click-circle-polygon-symbol-area-layer')) {
        map.setPaintProperty('click-circle-polygon-symbol-area-layer', 'text-opacity', this.s_drawOpacity)
      }
      if (map.getLayer('click-circle-line-layer')) {
        map.setPaintProperty('click-circle-line-layer', 'line-opacity', this.s_drawOpacity)
      }
      if (map.getLayer('click-circle-keiko-line-layer')) {
        map.setPaintProperty('click-circle-keiko-line-layer', 'line-opacity', this.s_drawOpacity)
      }
      if (map.getLayer('click-circle-label-layer')) {
        map.setPaintProperty('click-circle-label-layer', 'text-opacity', this.s_drawOpacity)
      }
      if (map.getLayer('click-circle-center-label-layer')) {
        map.setPaintProperty('click-circle-center-label-layer', 'text-opacity', this.s_drawOpacity)
      }
      if (map.getLayer('click-circle-symbol-layer')) {
        map.setPaintProperty('click-circle-symbol-layer', 'circle-opacity', this.s_drawOpacity)
      }
      if (map.getLayer('arrows-endpoint-layer')) {
        map.setPaintProperty('arrows-endpoint-layer', 'icon-opacity', this.s_drawOpacity)
      }
      if (map.getLayer('segment-label-layer')) {
        map.setPaintProperty('segment-label-layer', 'text-opacity', this.s_drawOpacity)
      }
    },
    // ドロー設定変更をconfigに保存
    configChangeDraw(prop, value) {
      const map = this.$store.state.map01
      if (!map) return
      this.$store.state.clickCircleGeojsonText = geojsonUpdate(
        map, null, clickCircleSource.iD, 'config', prop, value
      )
    },
    // ドロー全削除
    async deleteAllDraw() {
      // 共有ドロー（みんなで編集）の場合は削除不可（自分だけのドローは許可）
      if (this.$store.state.isUsingServerGeojson && this.$store.state.isEditable) {
        this.$store.dispatch('messageDialog/open', {
          id: 'draw-delete-all-error',
          title: '削除できません',
          contentHtml: '共有ドロー（みんなで編集）モードでは、全削除はできません。',
        })
        return
      }
      await deleteAll(false) // 確認ダイアログを表示
    },
    // 今昔マップ選択時にタイムスライダーを開くか確認
    async askOpenTimeSlider() {
      // すでにタイムスライダーが開いている場合は確認しない
      if (this.$store.state.floatingWindows['time-slider-window']) {
        return
      }
      const result = await vConfirm(
        '今昔タイムスライダーを開きますか？<br><small style="color:var(--oh3-color-text-secondary)">年代をスライダーで切り替えられます</small>',
        {
          title: '今昔マップ',
          okText: '開く',
          cancelText: '閉じる',
          icon: 'mdi-clock-outline',
          iconColor: 'primary'
        }
      )
      if (result) {
        this.$store.commit('setFloatingVisible', {
          id: 'time-slider-window',
          visible: true
        })
      }
    },
    // マイセット: ユーザーセット読み込み
    async loadMysetUserSets() {
      if (!this.userId) {
        this.mysetUserSets = []
        return
      }
      try {
        const fd = new FormData()
        fd.append('uid', this.userId)
        const response = await fetch('/oh3/php/userConfigSelect.php', { method: 'POST', body: fd })
        if (response.ok) {
          const data = await response.json()
          if (data && data[0] && data[0].user_popular_sets) {
            this.mysetUserSets = JSON.parse(data[0].user_popular_sets)
          } else {
            this.mysetUserSets = []
          }
        }
      } catch (e) {
        console.error('[Dialog-layer] ユーザーセット読み込みエラー:', e)
        this.mysetUserSets = []
      }
    },
    // マイセット: 現在のレイヤーとセットが一致するか判定
    isActiveSet(set) {
      const current = this.$store.state.selectedLayers?.[this.mapName] || []
      if (current.length === 0) return false
      const setLayerIds = set.layers.map(l => typeof l === 'string' ? l : l.id)
      const currentIds = current.map(l => l.id)
      // セットの全レイヤーが現在のレイヤーに含まれ、数も一致
      return setLayerIds.length === currentIds.length &&
        setLayerIds.every(id => currentIds.includes(id))
    },
    // マイセット: ワンタップ適用
    quickApplySet(set) {
      const setLayers = set.layers
      const newLayers = []
      const targetMap = this.mapName

      // ext系FloatingWindowを全て閉じる
      this.$store.state.ext[targetMap] = []
      // 既存のdialogInfoもクリア（extバッジをリセット）
      this.$store.state.dialogsInfo[targetMap] = []

      // 差分エンジンのスナップショットをクリア → フルリビルドを強制
      this.prevSnapshot[targetMap] = null

      // suppressExtAutoOpenを一時的にON → extダイアログはdialogInfoに登録されるがFWは非表示
      this.$store.state.suppressExtAutoOpen = true

      // 逆順で処理して正しい順序にする
      setLayers.slice().reverse().forEach(layer => {
        const layerId = typeof layer === 'string' ? layer : layer.id
        const savedOpacity = typeof layer === 'object' ? layer.opacity : undefined
        const savedVisibility = typeof layer === 'object' ? layer.visibility : undefined

        const findLayer = (nodes) => {
          if (!nodes || !Array.isArray(nodes)) return null
          for (const node of nodes) {
            if (node.id === layerId) return node
            if (node.nodes) {
              const found = findLayer(node.nodes)
              if (found) return found
            }
          }
          return null
        }

        const layerDef = findLayer(Layers.layers[targetMap])
        if (layerDef) {
          newLayers.unshift({
            ...layerDef,
            opacity: savedOpacity !== undefined ? savedOpacity : (layerDef.opacity || 1),
            visibility: savedVisibility !== undefined ? savedVisibility : (layerDef.visibility !== undefined ? layerDef.visibility : true),
          })
        }
      })

      this.$store.state.selectedLayers[targetMap] = newLayers
      requestAddLayers(this.$store, targetMap)

      // addLayers完了後にsuppressを解除
      setTimeout(() => {
        this.$store.state.suppressExtAutoOpen = false
      }, 2000)

      this.$toast?.success(`「${set.name}」に切替えました`)
    },
    // マイセット: チップ右クリックメニュー
    openChipMenu(event, set) {
      // システムセットは編集不可
      if (set._system) return
      this.chipMenu = { show: true, target: [event.clientX, event.clientY], set }
    },
    // マイセット: 名称変更ダイアログを開く
    openChipRename() {
      if (!this.chipMenu.set) return
      this.chipRenameValue = this.chipMenu.set.name
      this.chipRenameDialog = true
    },
    // マイセット: 名称変更を保存
    async saveChipRename() {
      if (!this.chipRenameValue || !this.chipMenu.set) return
      const set = this.mysetUserSets.find(s => s.id === this.chipMenu.set.id)
      if (set) {
        set.name = this.chipRenameValue
        this.rebuildMysetChips()
        await this.saveMysetOrder()
        this.$toast?.success('セット名を変更しました')
      }
      this.chipRenameDialog = false
    },
    // マイセット: チップから削除
    async deleteChipSet() {
      if (!this.chipMenu.set) return
      const name = this.chipMenu.set.name
      this.mysetUserSets = this.mysetUserSets.filter(s => s.id !== this.chipMenu.set.id)
      this.rebuildMysetChips()
      await this.saveMysetOrder()
      this.$toast?.success(`「${name}」を削除しました`)
    },
    // マイセット: チップ配列を再構築
    rebuildMysetChips() {
      this.mysetChips = [...this.mysetUserSets]
    },
    // マイセット: ドラッグ終了 → ユーザーセットの順序を抽出して保存
    onMysetDragEnd() {
      // mysetChipsはdraggableが直接更新済み
      // ユーザーセットだけ抽出して順序を保存
      this.mysetUserSets = this.mysetChips.filter(s => !s._system)
      this.saveMysetOrder()
    },
    // マイセット: 並べ替え保存
    async saveMysetOrder() {
      if (!this.userId) return
      try {
        const formData = new FormData()
        formData.append('uid', this.userId)
        formData.append('user_popular_sets', JSON.stringify(this.mysetUserSets))
        await fetch('/oh3/php/userPopularSetsUpdate.php', {
          method: 'POST',
          body: formData
        })
      } catch (e) {
        console.error('[Dialog-layer] セット順序保存エラー:', e)
      }
    },
    // マイセット: 新セット作成ダイアログを開く
    quickCreateSet() {
      const currentLayers = this.$store.state.selectedLayers?.[this.mapName] || []
      if (currentLayers.length === 0) {
        this.$toast?.warning('表示中のレイヤーがありません')
        return
      }
      this.mysetNewName = ''
      this.mysetCreateDialog = true
    },
    // マイセット: 保存実行
    async saveQuickSet() {
      if (!this.mysetNewName) return

      const currentLayers = this.$store.state.selectedLayers?.[this.mapName] || []
      const newSet = {
        id: `set-${Date.now()}`,
        name: this.mysetNewName,
        layers: currentLayers.map(l => ({
          id: l.id,
          opacity: l.opacity !== undefined ? l.opacity : 1,
          visibility: l.visibility !== undefined ? l.visibility : true
        })),
        created: new Date().toISOString(),
        updated: new Date().toISOString()
      }

      this.mysetUserSets.unshift(newSet)
      this.rebuildMysetChips()
      this.mysetCreateDialog = false

      // サーバーに保存
      try {
        const formData = new FormData()
        formData.append('uid', this.userId)
        formData.append('user_popular_sets', JSON.stringify(this.mysetUserSets))
        await fetch('/oh3/php/userPopularSetsUpdate.php', {
          method: 'POST',
          body: formData
        })
        this.$toast?.success('セットを保存しました')
      } catch (e) {
        console.error('[Dialog-layer] セット保存エラー:', e)
        this.$toast?.error('保存に失敗しました')
      }
    },
    // マイセットダイアログを開く
    openPopularSets() {
      this.$store.commit('setFloatingVisible', {
        id: `popular-sets-window-${this.mapName}`,
        visible: true
      })
    },
    // AI検索ダイアログを開く
    openAiSearchDialog() {
      // ログインチェック
      if (!this.userId) {
        showLoginPrompt({
          title: 'AI検索',
          message: 'AI検索はログインユーザー専用の機能です。<br>ご利用いただくには、ログインしてください。'
        })
        return
      }
      // AIマップクエリのFloatingWindowを開く（旧AI検索ダイアログの代わり）
      this.$store.commit('setFloatingVisible', {
        id: 'ai-map-query',
        visible: true
      })
    },
    // AI検索ダイアログを閉じる
    closeAiSearchDialog() {
      this.$store.commit('setFloatingVisible', {
        id: 'ai-layer-search-window',
        visible: false
      })
      this.showAiResults = false
    },
    // レイヤーツリーをフラット化してAI送信用に変換
    flattenLayersForAi(nodes, parentPath = '') {
      const result = []
      if (!nodes || !Array.isArray(nodes)) return result

      for (const node of nodes) {
        const label = node.label ? node.label.replace(/<[^>]*>/g, '') : ''
        const currentPath = parentPath ? `${parentPath} > ${label}` : label

        if (node.id) {
          result.push({
            id: node.id,
            label: label,
            path: currentPath
          })
        }

        const children = node.children || node.nodes
        if (children && children.length > 0) {
          result.push(...this.flattenLayersForAi(children, currentPath))
        }
      }
      return result
    },
    // AI検索のEnterキー処理（IME確定時は無視）
    handleAiSearchEnter(e) {
      // IME変換中は検索しない
      if (this.aiSearchComposing || e.isComposing) return
      this.executeAiSearch()
    },
    // レイヤーツリーから全レイヤーのid/labelを抽出
    // nodeMapには元のノード参照も保持（選択時に使用）
    extractAllLayers(nodes, nodeMap = null) {
      const result = []
      const traverse = (items) => {
        for (const item of items) {
          const children = item.children || item.nodes
          if (children && children.length > 0) {
            traverse(children)
          } else if (item.id && item.label) {
            // HTMLタグを除去
            const label = item.label.replace(/<[^>]*>/g, '')
            result.push({ id: item.id, label })
            // nodeMapが渡されたら元のノードを保持
            if (nodeMap) {
              nodeMap[item.id] = item
            }
          }
        }
      }
      traverse(nodes)
      return result
    },
    // AI検索を実行（Claude API使用）
    async executeAiSearch() {
      if (!this.aiSearchQuery.trim()) return

      this.aiSearching = true
      this.aiSearchResults = []
      this.aiSearchReason = ''
      this.aiSearchLayerMap = {}

      try {
        // クライアント側でレイヤーリストを生成（元ノード参照も保持）
        const layerList = this.extractAllLayers(this.layers, this.aiSearchLayerMap)

        const response = await fetch('/oh3/php/claudeLayerSearch.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: this.aiSearchQuery.trim(),
            layers: layerList
          })
        })

        const result = await response.json()

        if (result.success) {
          // AIが返したIDをそのまま使用
          this.aiSearchResults = result.matched_ids || []
          this.showAiResults = this.aiSearchResults.length > 0
          this.aiSearchReason = result.reason || ''

          // 検索結果を一番上にスクロール
          this.$nextTick(() => {
            setTimeout(() => {
              const fwEl = document.getElementById('ai-layer-search-window')
              const scrollArea = fwEl?.querySelector('.ai-search-scroll-area')
              if (scrollArea) {
                scrollArea.scrollTop = 0
              }
            }, 150)
          })
        } else {
          console.warn('AI検索エラー:', result.error, result.details)
          // APIエラーをユーザーに表示
          const errorMsg = this.formatAiError(result.error, result.details)
          showError(errorMsg, { title: 'AI検索エラー' })
          this.aiSearchReason = ''
        }
      } catch (error) {
        console.error('AI検索エラー:', error)
        showError('AI検索に失敗しました: ' + error.message, { title: 'AI検索エラー' })
        this.aiSearchReason = ''
      } finally {
        this.aiSearching = false
      }
    },
    // AI検索結果からレイヤーを選択
    selectAiResult(layer) {
      this.onNodeClick(layer)
      // ダイアログは閉じずに複数選択できるようにする
    },
    // AIエラーメッセージをフォーマット
    formatAiError(error, details) {
      if (details && details.type === 'rate_limit_error') {
        return 'APIリクエスト制限に達しました。<br>しばらく待ってから再度お試しください。'
      }
      if (details && details.message) {
        if (details.message.includes('rate_limit') || details.message.includes('too many')) {
          return 'APIリクエスト制限に達しました。<br>しばらく待ってから再度お試しください。'
        }
        return details.message
      }
      return error || 'AI検索に失敗しました'
    },
    // お気に入りレイヤーのみをフラットに抽出（フォルダなし）
    extractFavoriteLayers(nodes) {
      if (!nodes || !Array.isArray(nodes)) return []

      const favorites = []

      const traverse = (items) => {
        for (const item of items) {
          // 子を持つフォルダノード
          const children = item.children || item.nodes
          if (children && children.length > 0) {
            traverse(children)
          }
          // 葉ノード（実際のレイヤー）かつお気に入り
          else if (item.id && this.isFavorite(item.id)) {
            favorites.push(item)
          }
        }
      }

      traverse(nodes)
      return favorites
    },
    resetLayers() {
      // OSMベクターのレイヤー定義を取得
      const osmVectorLayer = this.findOsmVectorLayer(this.layers)

      if (osmVectorLayer) {
        // selectedLayersをOSMベクターのみにリセット
        const osmLayerObj = {
          id: osmVectorLayer.id,
          label: osmVectorLayer.label,
          source: osmVectorLayer.source,
          sources: osmVectorLayer.sources,
          layer: osmVectorLayer.layer,
          layers: osmVectorLayer.layers,
          attribution: osmVectorLayer.attribution,
          opacity: 1,
          visibility: true,
          ext: osmVectorLayer.ext,
          info: osmVectorLayer.info,
          position: osmVectorLayer.position,
        }

        this.s_selectedLayers[this.mapName] = [osmLayerObj]
        requestAddLayers(this.$store, this.mapName)
        this.zoomOperation(1000)
        this.saveHistory()
      }
    },

    handleUndo() {
      if (this.historyIndex > 0) {
        this.historyIndex--
        const previousState = JSON.parse(JSON.stringify(this.layerHistory[this.historyIndex]))
        this.s_selectedLayers[this.mapName] = previousState
        requestAddLayers(this.$store, this.mapName)
      }
    },

    handleRedo() {
      if (this.historyIndex < this.layerHistory.length - 1) {
        this.historyIndex++
        const nextState = JSON.parse(JSON.stringify(this.layerHistory[this.historyIndex]))
        this.s_selectedLayers[this.mapName] = nextState
        requestAddLayers(this.$store, this.mapName)
      }
    },

    async handleSaveStartupLayers() {
      const confirmed = await vConfirm(
        '現在のレイヤー状態を起動時レイヤーに設定しますか？',
        {
          title: '起動時レイヤー設定',
          okText: '設定する',
          cancelText: 'キャンセル',
          icon: 'mdi-content-save',
          color: 'primary'
        }
      )
      if (confirmed) {
        await startUrl()
      }
    },

    handleRestoreInitial() {
      if (!this.userId) {
        console.warn('[Dialog-layer] restoreInitial はユーザーIDがないため実行されません')
        return
      }
      // ログインユーザーのDB設定に戻る（他人のURLで起動していても自分の設定に戻す）
      const app = this.$root
      if (app && typeof app.restoreUserSettings === 'function') {
        app.restoreUserSettings({ alertOnMissing: true })
      } else {
        console.error('restoreUserSettings メソッドが見つかりません')
      }
    },

    saveHistory() {
      // Redo用の履歴を削除（現在位置より後ろを削除）
      this.layerHistory.splice(this.historyIndex + 1)

      // 新しい状態を追加
      const currentState = JSON.parse(JSON.stringify(this.s_selectedLayers[this.mapName]))
      this.layerHistory.push(currentState)

      // 最大サイズを超えたら古い履歴を削除
      if (this.layerHistory.length > this.maxHistorySize) {
        this.layerHistory.shift()
      } else {
        this.historyIndex++
      }
    },

    findOsmVectorLayer(nodes) {
      // 再帰的にOSMベクターレイヤーを検索
      if (!nodes || !Array.isArray(nodes)) return null
      for (const node of nodes) {
        if (node.id === 'oh-vector-layer-osm-bright') {
          return node
        }
        if (node.nodes && node.nodes.length > 0) {
          const found = this.findOsmVectorLayer(node.nodes)
          if (found) return found
        }
      }
      return null
    },

    handleClose() {
      // FloatingWindowの表示状態を更新
      this.$store.commit('setFloatingVisible', {
        id: 'layer-window-' + this.mapName,
        visible: false
      })
    },

    checkLabelOverflow() {
      // 全てのlabel-divをチェックして、はみ出ている場合はクラスを追加
      this.$nextTick(() => {
        // さらに少し待ってから測定（レイアウトが確定するまで）
        setTimeout(() => {
          const layers = this.s_selectedLayers[this.mapName] || []
          layers.forEach(element => {
            const refKey = 'label-' + element.id
            const ref = this.$refs[refKey]

            // refが配列の場合は最初の要素、そうでなければそのまま使用
            const labelDiv = Array.isArray(ref) ? ref[0] : ref

            if (labelDiv) {
              // labelDiv内の<br>タグを削除
              const brTags = labelDiv.querySelectorAll('br')
              brTags.forEach(br => br.remove())

              // labelがない場合はスキップ
              if (!element.label) return

              // 一時的な要素を作成してテキストの実際の幅を測定
              const tempSpan = document.createElement('span')
              tempSpan.style.visibility = 'hidden'
              tempSpan.style.position = 'absolute'
              tempSpan.style.whiteSpace = 'nowrap'
              tempSpan.style.fontSize = '15px'
              tempSpan.style.fontWeight = '500'
              // element.labelから<br>タグを除去
              tempSpan.innerHTML = element.label.replace(/<br\s*\/?>/gi, ' ')
              document.body.appendChild(tempSpan)

              const textWidth = tempSpan.offsetWidth
              const availableWidth = labelDiv.offsetWidth

              document.body.removeChild(tempSpan)

              // availableWidthが0の場合はスキップ（まだレンダリング中）
              if (availableWidth === 0) return

              // テキストの幅が利用可能な幅より大きい場合、2段表示にする
              if (textWidth > availableWidth) {
                labelDiv.classList.add('label-long')
              } else {
                labelDiv.classList.remove('label-long')
              }
            }
          })
        }, 100)
      })
    },
    hsEnabledMapsToggle(){
      const bool = !this.$store.state.hillshade.maps[this.mapName]
      this.$store.commit('SET_HS_FOR', { mapKey: this.mapName, enabled: bool })
    },
    scheduleAddLayers() {
      if (this.addLayersScheduleId !== null) return
      const scheduler = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
        ? window.setTimeout
        : setTimeout
      this.addLayersScheduleId = scheduler(() => {
        this.addLayersScheduleId = null
        this.addLayers()
      }, 0)
    },
    cancelScheduledAddLayers() {
      if (this.addLayersScheduleId === null) return
      const clearer = typeof window !== 'undefined' && typeof window.clearTimeout === 'function'
        ? window.clearTimeout
        : clearTimeout
      clearer(this.addLayersScheduleId)
      this.addLayersScheduleId = null
    },
    onStart () {
      this.isDragging = true
    },
    onEnd () {
      this.isDragging = false
      // ドラッグ終了時は、削除・追加ではなくレイヤー順序の変更のみを行う
      this.reorderLayers()
      this.zoomOperation(500)
      this.saveHistory()
    },
    async reorderLayers() {
      const map = this.$store.state[this.mapName]
      if (!map) return

      // s_selectedLayersが未定義の場合は処理しない
      const selectedLayers = this.s_selectedLayers[this.mapName]
      if (!selectedLayers || !Array.isArray(selectedLayers)) return

      // oh-レイヤーの後に来る最初の non-oh- レイヤーを見つける（ドローレイヤーなど）
      const allLayers = map.getStyle().layers
      let beforeId = null

      // oh-レイヤーを見つけた後、最初の non-oh- レイヤーを探す
      // pointcloud-* と oh-pc-depth-clear はdeck.gl管理レイヤーなのでスキップ
      if (allLayers) {
        let foundOhLayer = false
        for (let i = 0; i < allLayers.length; i++) {
          const layerId = allLayers[i].id
          if (layerId.slice(0, 2) === 'oh' || layerId.startsWith('pointcloud-')) {
            foundOhLayer = true
          } else if (foundOhLayer) {
            // oh-レイヤーの後に来る最初の non-oh- レイヤー（= ドローレイヤー）
            beforeId = layerId
            break
          }
        }
      }

      // s_selectedLayersを逆順でループ（配列の最後が最下層になるように）
      // moveLayer(id, beforeId)で指定したbeforeIdの前（下）に挿入
      // 逆順でループすることで配列の先頭が最終的に最上位（beforeIdの直下）になる
      for (let i = selectedLayers.length - 1; i >= 0; i--) {
        const layer = selectedLayers[i]

        // rrimプロパティを持つレイヤー（RRIM直接マウント）
        if (layer.rrim) {
          const customLayerId = `oh-mpi-rrim-${this.mapName}-${layer.id}`
          if (map.getLayer(customLayerId)) {
            map.moveLayer(customLayerId, beforeId)
          }
        }

        // extプロパティを持つレイヤー（カスタムレイヤー）の処理
        if (layer.ext) {
          if (layer.ext.name === 'extPointcloud') {
            // deck.gl interleaved モードのレイヤー（3D Tiles / COPC共通）
            // moveLayer()はdeck.glの_resolveLayers()に上書きされるため使えない
            // 代わりにbeforeIdプロパティを設定→deck.glが正しい位置に配置する
            let pcBeforeId = beforeId // デフォルト: 最上位（ドローレイヤーの直下）
            if (i > 0) {
              // 上のレイヤー(selectedLayers[i-1])の最初のMapLibreサブレイヤーIDを取得
              const layerAbove = selectedLayers[i - 1]
              if (layerAbove.layers && layerAbove.layers.length > 0) {
                pcBeforeId = layerAbove.layers[0].id
              } else if (layerAbove.ext) {
                // extレイヤーの場合、そのカスタムレイヤーIDを使用
                const aboveId = `oh-${layerAbove.ext.name.replace('ext-', '')}-${this.mapName}`
                if (map.getLayer(aboveId)) {
                  pcBeforeId = aboveId
                }
              }
            }
            if (layer.ext.pcType === '3dtiles') {
              setPointCloudBeforeId(map, layer.id, pcBeforeId)
            } else if (layer.ext.pcType === 'copc') {
              const { setCopcBeforeId } = await import('@/js/pointcloud/aistCopcManager')
              setCopcBeforeId(map, pcBeforeId)
            }
          } else {
            // カスタムレイヤーのIDパターン: oh-{拡張名}-{mapName}
            // 例: ext-mpi-rrim → oh-mpi-rrim-map01
            const customLayerId = `oh-${layer.ext.name.replace('ext-', '')}-${this.mapName}`
            if (map.getLayer(customLayerId)) {
              map.moveLayer(customLayerId, beforeId)
            } else {
              // ext-city等は独自レイヤーを作らず、layer.layersで定義されたレイヤーを使用するため
              // カスタムレイヤーが見つからなくても正常（layer.layersブロックで処理される）
              // layer.layersがない場合のみ警告を出す
              if (!layer.layers || layer.layers.length === 0) {
                console.warn('[reorderLayers] カスタムレイヤーが見つかりません:', customLayerId)
              }
            }
          }
        }

        // 通常のレイヤーの処理
        if (layer.layers && layer.layers.length > 0) {
          // 各レイヤーのサブレイヤーを順番にbeforeIdの前に移動
          layer.layers.forEach(layer0 => {
            if (map.getLayer(layer0.id)) {
              map.moveLayer(layer0.id, beforeId)
            }
          })
        }
      }

      // レイヤー順序変更後、URLを最新化
      this.$store.state.updatePermalinkFire = !this.$store.state.updatePermalinkFire
    },
    toggleCheck(element) {
      requestAddLayers(this.$store, this.mapName)
      const map = this.$store.state[this.mapName]
      element.layers.forEach(layer0 => {
        let visible
        if (element.visibility) {
          visible = 'visible'
        } else {
          visible = 'none'
        }
        map.setLayoutProperty(layer0.id, 'visibility', visible)
      })
      element.visibility = !element.visibility
      // const layers = map.getStyle().layers;
      // const firstLayerId = layers.length > 0 ? layers[0].id : null;
      // map.moveLayer('sima-borders', firstLayerId)
      // map.moveLayer('sima-layer', firstLayerId)

      this.zoomOperation(1000)
      // プログラムの最後に追加
      const layers = map.getStyle().layers;
      const lastLayerId = layers.length > 0 ? layers[layers.length - 1].id : null;
      if (map.getLayer('sima-layer') && lastLayerId) {
        map.moveLayer('sima-layer',lastLayerId);
      }
    },
    infoOpen (element,isNew,isBlock) {
      const result = this.s_dialogsINfo[this.mapName].find(el => el.id === element.id)

      // FloatingWindowのIDを生成
      const floatingWindowId = 'dialog-ext-' + this.mapName + '-' + element.id

      if (!result) {
        // 新規ダイアログの場合のみ位置を計算
        const dialogEl = document.querySelector('#layer-window-' + this.mapName)

        // マップコンテナの位置を取得（2画面モード対応）
        const mapContainer = document.getElementById(this.mapName)
        const mapRect = mapContainer ? mapContainer.getBoundingClientRect() : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight }

        // FloatingWindowが表示されているかをVuexとDOMの両方でチェック
        const windowId = 'layer-window-' + this.mapName
        const isDialogLayerVisible = this.$store.state.floatingWindows?.[windowId] &&
                                       dialogEl &&
                                       dialogEl.offsetWidth > 0

        // 基本位置を設定（ビューポート座標で計算）
        let top
        let left
        if (isDialogLayerVisible) {
          // Dialog-layerが表示されている場合（ビューポート座標を使用）
          const rect = dialogEl.getBoundingClientRect()
          top = rect.top + 'px'
          // マップエリアの幅を考慮して位置を計算
          const mapAreaWidth = mapRect.width
          left = mapAreaWidth > 500
            ? (rect.left + rect.width + 5) + 'px'
            : (mapRect.left + 10) + 'px'
        } else {
          // Dialog-layerが非表示の場合（起動時）
          // マップコンテナの左上を基準にする
          top = (mapRect.top + 100) + 'px'
          left = (mapRect.left + 10) + 'px'
        }

        // 既存ダイアログの有無でずらし処理
        const existingDialogs = this.s_dialogsINfo[this.mapName].filter(d => d.id !== element.id)
        if (existingDialogs.length > 0) {
          // 既存ダイアログがある場合：最後のダイアログから右下に20pxずらす
          const lastDialog = existingDialogs[existingDialogs.length - 1]
          const lastTop = parseInt(lastDialog.style.top) || (mapRect.top + 100)
          const lastLeft = parseInt(lastDialog.style.left) || (mapRect.left + 10)
          top = (lastTop + 20) + 'px'
          left = (lastLeft + 20) + 'px'
        } else {
          // 既存ダイアログがない場合：マップ固有のinfoCountを使ってずらす
          top = Number(top.replace('px','')) + (infoCount[this.mapName] * 20) + 'px'
          left = Number(left.replace('px','')) + (infoCount[this.mapName] * 20) + 'px'
          infoCount[this.mapName]++
        }

        // 画面外チェック：extダイアログの幅を考慮（最大400px）
        // マップエリアの右端を基準にする
        const estimatedWidth = 400
        const maxLeft = mapRect.left + mapRect.width - estimatedWidth - 20 // マップエリアの右端から20pxのマージン
        const minLeft = mapRect.left + 10 // マップエリアの左端から10pxのマージン
        const leftNum = parseInt(left)
        if (leftNum > maxLeft || leftNum < minLeft) {
          left = (mapRect.left + 10) + 'px'
          top = (mapRect.top + 100) + 'px'
        }

        // 新規ダイアログ作成
        const infoDialog = {
          ...element,
          style: {
            width: 'auto',
            top: top,
            left: left,
            'z-index': getNextZIndex()
          }
        }
        this.$store.commit('pushDialogsInfo', {mapName: this.mapName, dialog: infoDialog})

        // FloatingWindowがマウントされた後に表示
        if (isBlock) {
          this.$nextTick(() => {
            this.$store.commit('setFloatingVisible', { id: floatingWindowId, visible: true })
          })
        }
      } else {
        // 既存ダイアログを再表示
        if (isNew) {
          result.style["z-index"] = getNextZIndex()
          this.$nextTick(() => {
            this.$store.commit('setFloatingVisible', { id: floatingWindowId, visible: true })
          })
        }
      }
    },
    changeWatchFlg (bool) {
      if (bool) {
        requestAddLayers(this.$store, this.mapName)
      }
    },
    changeSlider () {
      this.zoomOperation(500)
    },
    inputSlider (element){
      const map = this.$store.state[this.mapName]
      applyOpacityAndVisibility(map, element)
    },
    zoomOperation (timeOut) {
      // // なぜか一度ズームしないと最適化ベクタータイルが反映しない。
      const map = this.$store.state[this.mapName]
      const zoom = map.getZoom()
      setTimeout(() => {
        map.setZoom(12)
        map.setZoom(zoom)
        const layers = map.getStyle().layers;
        const lastLayerId = layers.length > 0 ? layers[layers.length - 1].id : null;
        if (map.getLayer('sima-layer') && lastLayerId) {
          map.moveLayer('sima-layer',lastLayerId);
          map.moveLayer('sima-borders',lastLayerId);
        }
      },timeOut)
    },
    onKebabClick(element) {
      // 既存のFWがあれば表示するだけ
      const existing = this.jumpDialogs.find(d => d.layerId === element.id)
      if (existing) {
        const fwId = 'jump-dialog-' + this.mapName + '-' + element.id
        this.$store.commit('setFloatingVisible', { id: fwId, visible: true })
        return
      }

      // 新規FW作成
      const map = this.$store.state[this.mapName]
      const offset = this.jumpDialogs.length * 20
      const jd = {
        layerId: element.id,
        label: element.label,
        lat: map ? Math.round(map.getCenter().lat * 1000000) / 1000000 : 0,
        lng: map ? Math.round(map.getCenter().lng * 1000000) / 1000000 : 0,
        zoom: map ? Math.round(map.getZoom() * 10) / 10 : 10,
        top: 100 + offset,
        left: 400 + offset,
      }
      this.jumpDialogs.push(jd)

      // mapのmoveイベントで座標を自動更新
      this.startJumpDialogMapListener()

      setTimeout(() => {
        const fwId = 'jump-dialog-' + this.mapName + '-' + jd.layerId
        this.$store.commit('setFloatingVisible', { id: fwId, visible: true })
      }, 300)
    },
    closeJumpDialog(layerId) {
      this.jumpDialogs = this.jumpDialogs.filter(d => d.layerId !== layerId)
      // 全FW閉じたらリスナー解除
      if (this.jumpDialogs.length === 0) {
        this.stopJumpDialogMapListener()
      }
    },
    // mapのmoveendイベントで開いている全jumpDialogsの座標を更新
    startJumpDialogMapListener() {
      if (this._jumpMapListener) return
      const map = this.$store.state[this.mapName]
      if (!map) return
      this._jumpMapListener = () => {
        const center = map.getCenter()
        const lat = Math.round(center.lat * 1000000) / 1000000
        const lng = Math.round(center.lng * 1000000) / 1000000
        const zoom = Math.round(map.getZoom() * 10) / 10
        this.jumpDialogs.forEach(jd => {
          jd.lat = lat
          jd.lng = lng
          jd.zoom = zoom
        })
      }
      map.on('moveend', this._jumpMapListener)
    },
    stopJumpDialogMapListener() {
      if (!this._jumpMapListener) return
      const map = this.$store.state[this.mapName]
      if (map) {
        map.off('moveend', this._jumpMapListener)
      }
      this._jumpMapListener = null
    },
    flyToJumpLocation(layerId) {
      const loc = this.jumpLocations[layerId]
      if (!loc) return
      const map = this.$store.state[this.mapName]
      if (!map) return
      map.flyTo({
        center: [loc.lng, loc.lat],
        zoom: loc.zoom,
        speed: 1.2,
        curve: 1.42,
        essential: true
      })
    },
    async saveJumpLocationFor(jd) {
      console.log('[Jump Save] 開始', { isGeneralUser: this.isGeneralUser, isTeacher: this.$store.state.isTeacher, isStudent: this.$store.state.isStudent, userId: this.$store.state.userId, jd })
      if (this.isGeneralUser) {
        // 一般ユーザー: user_jump_locations に保存
        const uid = this.$store.state.userId
        console.log('[Jump Save] 一般ユーザー uid:', uid)
        if (!uid) { console.log('[Jump Save] uid が空のため return'); return }
        try {
          console.log('[Jump Save] userJumpSave.php に送信中...')
          const result = await phpApiPost('userJumpSave.php', {
            firebase_uid: uid,
            layer_id: jd.layerId,
            nickname: this.$store.state.myNickname || null,
            lat: jd.lat,
            lng: jd.lng,
            zoom: jd.zoom
          })
          console.log('[Jump Save] レスポンス:', result)
          if (result.status === 'success') {
            this.jumpLocations = {
              ...this.jumpLocations,
              [jd.layerId]: { lat: jd.lat, lng: jd.lng, zoom: jd.zoom }
            }
            this.closeJumpDialog(jd.layerId)
          }
        } catch (e) {
          console.error('ジャンプ位置保存エラー:', e)
        }
      } else {
        // 教員: layer_jump_locations に保存（従来通り）
        const uid = this.$store.state.teacherInfo?.firebase_uid
        console.log('[Jump Save] 教員 uid:', uid)
        if (!uid) { console.log('[Jump Save] 教員uid が空のため return'); return }
        try {
          console.log('[Jump Save] layerJumpSave.php に送信中...')
          const result = await phpApiPost('layerJumpSave.php', {
            firebase_uid: uid,
            layer_id: jd.layerId,
            lat: jd.lat,
            lng: jd.lng,
            zoom: jd.zoom
          })
          if (result.status === 'success') {
            this.jumpLocations = {
              ...this.jumpLocations,
              [jd.layerId]: { lat: jd.lat, lng: jd.lng, zoom: jd.zoom }
            }
            this.closeJumpDialog(jd.layerId)
          }
        } catch (e) {
          console.error('ジャンプ位置保存エラー:', e)
        }
      }
    },
    async deleteJumpLocationFor(jd) {
      if (this.isGeneralUser) {
        // 一般ユーザー
        const uid = this.$store.state.userId
        if (!uid) return
        try {
          const result = await phpApiPost('userJumpDelete.php', {
            firebase_uid: uid,
            layer_id: jd.layerId
          })
          if (result.status === 'success') {
            const updated = { ...this.jumpLocations }
            delete updated[jd.layerId]
            this.jumpLocations = updated
            this.closeJumpDialog(jd.layerId)
          }
        } catch (e) {
          console.error('ジャンプ位置削除エラー:', e)
        }
      } else {
        // 教員（従来通り）
        const uid = this.$store.state.teacherInfo?.firebase_uid
        if (!uid) return
        try {
          const result = await phpApiPost('layerJumpDelete.php', {
            firebase_uid: uid,
            layer_id: jd.layerId
          })
          if (result.status === 'success') {
            const updated = { ...this.jumpLocations }
            delete updated[jd.layerId]
            this.jumpLocations = updated
            this.closeJumpDialog(jd.layerId)
          }
        } catch (e) {
          console.error('ジャンプ位置削除エラー:', e)
        }
      }
    },
    async loadJumpLocations() {
      const layers = this.s_selectedLayers[this.mapName]
      if (!layers || layers.length === 0) return
      const layerIds = layers.map(l => l.id)

      if (this.isGeneralUser) {
        // 一般ユーザー: firebase_uid で自分のジャンプ位置を取得
        const uid = this.$store.state.userId
        if (!uid) return
        try {
          const result = await phpApiGet('userJumpSelect.php', {
            firebase_uid: uid,
            layer_ids: JSON.stringify(layerIds)
          })
          if (result.status === 'success') {
            this.jumpLocations = result.jumpLocations || {}
          }
        } catch (e) {
          console.error('ジャンプ位置取得エラー:', e)
        }
      } else {
        // 教員/生徒: teacher_id で取得（従来通り）
        const teacherId = this.effectiveTeacherId
        if (!teacherId) return
        try {
          const result = await phpApiGet('layerJumpSelect.php', {
            teacher_id: teacherId,
            layer_ids: JSON.stringify(layerIds)
          })
          if (result.status === 'success') {
            this.jumpLocations = result.jumpLocations || {}
          }
        } catch (e) {
          console.error('ジャンプ位置取得エラー:', e)
        }
      }
    },
    // ── メモ機能 ──
    onMemoClick(element) {
      const existing = this.memoDialogs.find(d => d.layerId === element.id)
      if (existing) {
        const fwId = 'memo-dialog-' + this.mapName + '-' + element.id
        this.$store.commit('setFloatingVisible', { id: fwId, visible: true })
        return
      }
      const offset = this.memoDialogs.length * 20
      const memo = this.layerMemos[element.id]
      const md = {
        layerId: element.id,
        label: element.label,
        memoText: memo ? memo.memo : '',
        top: 120 + offset,
        left: 420 + offset,
        readOnly: false,
      }
      this.memoDialogs.push(md)
      setTimeout(() => {
        const fwId = 'memo-dialog-' + this.mapName + '-' + md.layerId
        this.$store.commit('setFloatingVisible', { id: fwId, visible: true })
      }, 300)
    },
    onMemoIconClick(element) {
      // 生徒は閲覧専用で開く、教員・一般は編集可能で開く
      const existing = this.memoDialogs.find(d => d.layerId === element.id)
      if (existing) {
        const fwId = 'memo-dialog-' + this.mapName + '-' + element.id
        this.$store.commit('setFloatingVisible', { id: fwId, visible: true })
        return
      }
      const isStudent = this.$store.state.isStudent
      const offset = this.memoDialogs.length * 20
      const memo = this.layerMemos[element.id]
      const md = {
        layerId: element.id,
        label: element.label,
        memoText: memo ? memo.memo : '',
        top: 120 + offset,
        left: 420 + offset,
        readOnly: isStudent,
      }
      this.memoDialogs.push(md)
      setTimeout(() => {
        const fwId = 'memo-dialog-' + this.mapName + '-' + md.layerId
        this.$store.commit('setFloatingVisible', { id: fwId, visible: true })
      }, 300)
    },
    closeMemoDialog(layerId) {
      this.memoDialogs = this.memoDialogs.filter(d => d.layerId !== layerId)
    },
    async saveMemoFor(md) {
      if (!md.memoText || md.memoText.trim() === '') return
      if (this.isGeneralUser) {
        const uid = this.$store.state.userId
        if (!uid) return
        try {
          const result = await phpApiPost('userMemoSave.php', {
            firebase_uid: uid,
            layer_id: md.layerId,
            nickname: this.$store.state.myNickname || null,
            memo_text: md.memoText.trim()
          })
          if (result.status === 'success') {
            this.layerMemos = {
              ...this.layerMemos,
              [md.layerId]: { memo: md.memoText.trim() }
            }
            this.closeMemoDialog(md.layerId)
          }
        } catch (e) {
          console.error('メモ保存エラー:', e)
        }
      } else {
        const uid = this.$store.state.teacherInfo?.firebase_uid
        if (!uid) return
        try {
          const result = await phpApiPost('layerMemoSave.php', {
            firebase_uid: uid,
            layer_id: md.layerId,
            memo_text: md.memoText.trim()
          })
          if (result.status === 'success') {
            this.layerMemos = {
              ...this.layerMemos,
              [md.layerId]: { memo: md.memoText.trim() }
            }
            this.closeMemoDialog(md.layerId)
          }
        } catch (e) {
          console.error('メモ保存エラー:', e)
        }
      }
    },
    async deleteMemoFor(md) {
      if (this.isGeneralUser) {
        const uid = this.$store.state.userId
        if (!uid) return
        try {
          const result = await phpApiPost('userMemoDelete.php', {
            firebase_uid: uid,
            layer_id: md.layerId
          })
          if (result.status === 'success') {
            const updated = { ...this.layerMemos }
            delete updated[md.layerId]
            this.layerMemos = updated
            this.closeMemoDialog(md.layerId)
          }
        } catch (e) {
          console.error('メモ削除エラー:', e)
        }
      } else {
        const uid = this.$store.state.teacherInfo?.firebase_uid
        if (!uid) return
        try {
          const result = await phpApiPost('layerMemoDelete.php', {
            firebase_uid: uid,
            layer_id: md.layerId
          })
          if (result.status === 'success') {
            const updated = { ...this.layerMemos }
            delete updated[md.layerId]
            this.layerMemos = updated
            this.closeMemoDialog(md.layerId)
          }
        } catch (e) {
          console.error('メモ削除エラー:', e)
        }
      }
    },
    async loadLayerMemos() {
      const layers = this.s_selectedLayers[this.mapName]
      if (!layers || layers.length === 0) return
      const layerIds = layers.map(l => l.id)

      if (this.isGeneralUser) {
        const uid = this.$store.state.userId
        if (!uid) return
        try {
          const result = await phpApiGet('userMemoSelect.php', {
            firebase_uid: uid,
            layer_ids: JSON.stringify(layerIds)
          })
          if (result.status === 'success') {
            this.layerMemos = result.memos || {}
          }
        } catch (e) {
          console.error('メモ取得エラー:', e)
        }
      } else {
        // 教員/生徒: teacher_id で取得
        const teacherId = this.effectiveTeacherId
        if (!teacherId) return
        try {
          const result = await phpApiGet('layerMemoSelect.php', {
            teacher_id: teacherId,
            layer_ids: JSON.stringify(layerIds)
          })
          if (result.status === 'success') {
            this.layerMemos = result.memos || {}
          }
        } catch (e) {
          console.error('メモ取得エラー:', e)
        }
      }
    },
    removeLayer(id){
      // 削除対象のレイヤーオブジェクトを先に取得（s_selectedLayersから消す前に）
      const targetLayer = this.s_selectedLayers[this.mapName]?.find(layer => layer.id === id)
      const map = this.$store.state[this.mapName]

      // 1. mapからレイヤー/ソースを直接削除（フルリビルド不要）
      if (map && targetLayer) {
        this.removeLayerAndSource(map, targetLayer)
      }

      // RRIM直接マウント解除
      if (targetLayer && targetLayer.rrim) {
        this.$store.state.rrimItems[this.mapName] = this.$store.state.rrimItems[this.mapName].filter(item => item.id !== id)
      }

      // 2. VuexのselectedLayersからフィルタ削除
      this.s_selectedLayers[this.mapName] = this.s_selectedLayers[this.mapName].filter(layer => layer.id !== id)
      this.$store.state.dialogsInfo[this.mapName] = this.$store.state.dialogsInfo[this.mapName].filter(layer => layer.id !== id)

      // FloatingWindowの状態もクリア（再追加時の表示問題を防ぐ）
      const floatingWindowId = 'dialog-ext-' + this.mapName + '-' + id
      delete this.$store.state.floatingWindows[floatingWindowId]

      // スナップショットを更新（差分パスの整合性を保つ）
      const selectedLayersAfter = this.s_selectedLayers[this.mapName] || []
      this.prevSnapshot[this.mapName] = computeLayerSnapshot(selectedLayersAfter)
      this.prevFullLayers[this.mapName] = selectedLayersAfter

      // addLayersRequestedは立てない（mapから直接削除済みなのでaddLayersによるフルリビルド不要）
      // deep watcherからscheduleAddLayersは呼ばれるが、カウンター=0なのでaddLayersはスキップされる

      if (id === 'oh-jpg-layer') {
        this.$store.state.uploadedImage = ''
      }

      this.zoomOperation(1000)
      this.saveHistory()

      // パーマリンク更新
      this.$store.state.updatePermalinkFire = !this.$store.state.updatePermalinkFire
    },
    onNodeClick (node) {
      if (node.layers) {
        if (node.id) {
          this.triggerLayerClickHighlight(node.id)
        }
        // Track layer selection/deselection
        if (node.id) {
          const cleanLabel = node.label ? node.label.replace(/<[^>]*>/g, '').trim() : node.id
          const isAlreadySelected = this.s_selectedLayers[this.mapName]?.find(
            layers => layers.id === node.id
          )

          this.usageTracker?.trackEvent(
            'layer',
            isAlreadySelected ? 'deselect' : 'select',
            node.id,
            {
              name: cleanLabel,
              mapName: this.mapName,
              action: isAlreadySelected ? 'removed' : 'added'
            }
          )
        }

        if (node.position) this.nodeClicked = true
        if (node.flyZoom) this.flyZoom = node.flyZoom
        const map = this.$store.state[this.mapName]

        const existingLayerIndex = this.s_selectedLayers[this.mapName].findIndex(layers => layers.id === node.id)

        if (existingLayerIndex !== -1) {
          // 既に選択済みならトグルで削除（mapから直接削除するのでaddLayers不要）
          this.removeLayer(node.id)
          return
        }

        // 新規追加の場合のみaddLayersをリクエスト（フルリビルドが必要）
        requestAddLayers(this.$store, this.mapName)

        if(existingLayerIndex === -1) {
          // 新規追加の場合
          const obj = {
            id: node.id,
            label: node.label,
            source: node.source,
            sources: node.sources,
            layer: node.layer,
            layers: node.layers,
            attribution: node.attribution,
            opacity: 1,
            visibility: true,
            ext: node.ext,
            rrim: node.rrim,
            info: node.info,
            position: node.position,
          }
          // RRIM直接マウント（ext経由を廃止）
          if (node.rrim) {
            this.$store.state.rrimItems[this.mapName].push(obj)
          }
          if (!this.s_selectedLayers[this.mapName]?.some(layer => layer.id === 'oh-amx-a-fude' || layer.id === 'oh-homusyo-2025-layer')) {
            this.s_selectedLayers[this.mapName].unshift(obj)
          } else {
            this.s_selectedLayers[this.mapName].splice(1, 0, obj)
          }

          // 最近使用したレイヤーに追加（新規追加時のみ）
          this.$store.dispatch('addRecentLayer', node.id)

          // 今昔マップの場合はタイムスライダーを開くか確認
          if (node.id && node.id.startsWith('oh-kon-')) {
            this.askOpenTimeSlider()
          }
        }

        this.zoomOperation(1000)
        this.saveHistory()

        // 小画面時はDialog-layerを閉じる
        if (window.innerWidth < FULLSCREEN_DIALOG_WIDTH_THRESHOLD) {
          this.$store.commit('setFloatingVisible', {
            id: 'layer-window-' + this.mapName,
            visible: false
          })
        }
      }
    },
    triggerLayerClickHighlight(layerId) {
      if (!layerId) return
      if (this.layerClickHighlightTimer) {
        clearTimeout(this.layerClickHighlightTimer)
        this.layerClickHighlightTimer = null
      }
      this.layerClickHighlightId = layerId
      this.layerClickHighlightTimer = setTimeout(() => {
        this.layerClickHighlightId = null
        this.layerClickHighlightTimer = null
      }, 450)
    },
    getRecentPinStorageKey() {
      const userId = this.currentUserId
      return `oh3-recent-layers-pins-${userId}-${this.mapName}`
    },
    loadRecentLayerPins() {
      try {
        const saved = localStorage.getItem(this.getRecentPinStorageKey())
        if (saved) {
          const parsed = JSON.parse(saved)
          this.pinnedRecentLayerIds = Array.isArray(parsed) ? parsed : []
        } else {
          this.pinnedRecentLayerIds = []
        }
      } catch (error) {
        console.warn('Failed to load recent layer pins:', error)
        this.pinnedRecentLayerIds = []
      }
    },
    saveRecentLayerPins() {
      try {
        localStorage.setItem(this.getRecentPinStorageKey(), JSON.stringify(this.pinnedRecentLayerIds))
      } catch (error) {
        console.warn('Failed to save recent layer pins:', error)
      }
    },
    isRecentLayerPinned(layerId) {
      return this.pinnedRecentLayerIds.includes(layerId)
    },
    toggleRecentLayerPin(layerId) {
      if (!layerId) return
      if (this.isRecentLayerPinned(layerId)) {
        this.pinnedRecentLayerIds = this.pinnedRecentLayerIds.filter(id => id !== layerId)
      } else {
        this.pinnedRecentLayerIds = [layerId, ...this.pinnedRecentLayerIds]
      }
      this.saveRecentLayerPins()
      this.cachedRecentLayers = this.recentLayersForDisplay
    },
    removeRecentLayer(layerId) {
      if (!layerId) return
      const next = this.recentLayerIds.filter(id => id !== layerId)
      this.$store.commit('SET_RECENT_LAYERS', next)
      this.pinnedRecentLayerIds = this.pinnedRecentLayerIds.filter(id => id !== layerId)
      const userId = localStorage.getItem('lastUserId') || 'guest'
      if (userId && userId !== 'guest') {
        try {
          const nickname = localStorage.getItem('lastNickname') || ''
          const params = new URLSearchParams()
          params.append('uid', userId)
          params.append('recent_layer_ids', JSON.stringify(next))
          params.append('nickname', nickname)
          fetch('/oh3/php/userConfig.php', {
            method: 'POST',
            body: params,
          }).catch(() => {
            localStorage.setItem('oh3.recentLayerIds', JSON.stringify(next))
          })
        } catch (error) {
          console.warn('Failed to save recent layers:', error)
          localStorage.setItem('oh3.recentLayerIds', JSON.stringify(next))
        }
      } else {
        try {
          localStorage.setItem('oh3.recentLayerIds', JSON.stringify(next))
        } catch (error) {
          console.warn('Failed to save recent layers:', error)
        }
      }
      this.saveRecentLayerPins()
      this.cachedRecentLayers = this.recentLayersForDisplay
    },
    startRecentLayersResize(event) {
      const clientY = event.touches ? event.touches[0].clientY : event.clientY
      this.isResizingRecentLayers = true
      this.recentLayersResizeStartY = clientY
      this.recentLayersResizeStartHeight = this.recentLayersHeight
      window.addEventListener('mousemove', this.onRecentLayersResizeMove)
      window.addEventListener('mouseup', this.stopRecentLayersResize)
      window.addEventListener('touchmove', this.onRecentLayersResizeMove, { passive: false })
      window.addEventListener('touchend', this.stopRecentLayersResize)
    },
    onRecentLayersResizeMove(event) {
      if (!this.isResizingRecentLayers) return
      const clientY = event.touches ? event.touches[0].clientY : event.clientY
      const delta = clientY - this.recentLayersResizeStartY
      const nextHeight = this.recentLayersResizeStartHeight + delta
      const minHeight = 50
      const maxHeight = 360
      this.recentLayersHeight = Math.max(minHeight, Math.min(maxHeight, nextHeight))
    },
    stopRecentLayersResize() {
      if (!this.isResizingRecentLayers) return
      this.isResizingRecentLayers = false
      localStorage.setItem('oh3-recent-layers-height', String(this.recentLayersHeight))
      window.removeEventListener('mousemove', this.onRecentLayersResizeMove)
      window.removeEventListener('mouseup', this.stopRecentLayersResize)
      window.removeEventListener('touchmove', this.onRecentLayersResizeMove)
      window.removeEventListener('touchend', this.stopRecentLayersResize)
    },
    // ---- 差分レイヤーシステム ヘルパーメソッド ----
    applyPropertyChanges(map, changes, selectedLayers) {
      for (const change of changes) {
        const layer = selectedLayers.find(l => l.id === change.id)
        if (!layer || !layer.layers) continue
        // selectedLayersは既にVuexのcurrent stateなのでlayerの値を直接使う
        applyOpacityAndVisibility(map, layer)
      }
    },
    removeLayerAndSource(map, layer) {
      // レイヤーを削除
      if (layer.layers) {
        layer.layers.forEach(layer0 => {
          if (map.getLayer(layer0.id)) {
            try { map.removeLayer(layer0.id) } catch (e) { /* ignore */ }
          }
        })
      }
      // ソースも削除（レイヤー削除後でないとremoveSourceが失敗する）
      // 他のレイヤーが使用中のソースは削除しない
      const safeRemoveSource = (sourceId) => {
        if (!sourceId || !map.getSource(sourceId)) return
        const styleLayers = map.getStyle()?.layers || []
        const usedBy = styleLayers.filter(l => l.source === sourceId)
        if (usedBy.length === 0) {
          try { map.removeSource(sourceId) } catch (e) { /* ignore */ }
        }
      }
      if (layer.sources) {
        layer.sources.forEach(source => {
          safeRemoveSource(source.id)
        })
      }
      if (layer.source && typeof layer.source === 'object') {
        safeRemoveSource(layer.source.id)
      }
    },
    applyStructuralChanges(map, diff, selectedLayers) {
      if (diff.orderOnly) {
        // 順序のみ変更 → reorderLayersで対応
        return
      }
      // 内部レイヤー変更（地番図の動的追加等）→ 該当レイヤーを再追加
      if (diff.layersChanged) {
        for (const layer of selectedLayers) {
          this.addSingleLayer(map, layer)
        }
        return
      }
      // 削除: removedのレイヤーをmapから削除
      if (diff.removed) {
        const prevSelectedLayers = this.prevFullLayers[this.mapName] || []
        for (const removedSnap of diff.removed) {
          const layer = prevSelectedLayers.find(l => l.id === removedSnap.id)
          if (layer) {
            this.removeLayerAndSource(map, layer)
          }
        }
      }
      // 追加: addedのレイヤーをmapに追加
      if (diff.added) {
        for (const addedSnap of diff.added) {
          const layer = selectedLayers.find(l => l.id === addedSnap.id)
          if (layer) {
            this.addSingleLayer(map, layer)
          }
        }
      }
    },
    registerChibanClickHandlers(map) {
      if (map !== this.$store.state.map01) return
      setTimeout(() => {
        const targetLayers = map.getStyle().layers
            .filter(layer => (layer.id.startsWith('oh-chiban') || layer.id.startsWith('oh-chibanzu')) && !registeredLayers.has(layer.id))
            .map(layer => layer.id)
        if (targetLayers.length === 0) return
        targetLayers.forEach(layerId => {
          registeredLayers.add(layerId)
          map.on('click', layerId, (e) => {
            if (this.$store.state.isDraw) return
            const featuresOnPlateau = map.queryRenderedFeatures(e.point, { layers: ['oh-plateau-tokyo23ku-layer'] })
            if (featuresOnPlateau && featuresOnPlateau.length > 0) return
            if (e.features && e.features.length > 0) {
              let smallestFeature = e.features[0]
              let minArea = turf.area(e.features[0])
              for (let feature of e.features) {
                const area = turf.area(feature)
                if (area < minArea) {
                  smallestFeature = feature
                  minArea = area
                }
              }
              let targetId
              if (smallestFeature.properties['Shozai'] !== undefined && smallestFeature.properties['Chiban'] !== undefined) {
                targetId = `${smallestFeature.properties['Shozai']}_${smallestFeature.properties['Chiban']}`
              } else if (smallestFeature.properties['id'] !== undefined) {
                targetId = `${smallestFeature.properties['id']}`
              } else {
                targetId = `${smallestFeature.properties['oh3id']}`
              }
              if (this.$store.state.highlightedChibans.has(targetId)) {
                this.$store.state.highlightedChibans.delete(targetId)
                store.commit('setRightDrawer', false)
              } else {
                this.$store.state.highlightedChibans.add(targetId)
                const lngLat = e.lngLat
                store.state.popupFeature = {
                  type: smallestFeature.type,
                  geometry: smallestFeature.geometry ? JSON.parse(JSON.stringify(smallestFeature.geometry)) : null,
                  properties: smallestFeature.properties ? JSON.parse(JSON.stringify(smallestFeature.properties)) : {}
                }
                store.state.popupFeatureProperties = smallestFeature.properties
                store.state.popupFeatureCoordinates = [lngLat.lng, lngLat.lat]
                ;(async () => {
                  const address = await LngLatToAddress(lngLat.lng, lngLat.lat)
                  store.state.rightDrawerTitle = address
                })()
                store.commit('setChibanzuDrawer', false)
                store.commit('setRightDrawer', true)
              }
              highlightSpecificFeaturesCity(map, layerId)
            }
          })
        })
      }, 0)
    },
    addSingleLayer(map, layer) {
      if (!layer.layers) return
      const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)

      // ソース追加
      if (layer.sources) {
        layer.sources.forEach(source => {
          if (!map.getSource(source.id)) {
            try {
              const sourceObj = source.getTiles
                ? { ...source.obj, tiles: source.getTiles() }
                : source.obj
              map.addSource(source.id, sourceObj)
            } catch (e) { /* ignore */ }
          }
        })
      }
      if (layer.source) {
        if (isObject(layer.source)) {
          if (!map.getSource(layer.source.id)) {
            try {
              const sourceObj = layer.source.getTiles
                ? { ...layer.source.obj, tiles: layer.source.getTiles() }
                : layer.source.obj
              map.addSource(layer.source.id, sourceObj)
            } catch (e) { /* ignore */ }
          }
        } else {
          if (!map.getSource(layer.source)) {
            const source = this.$store.state.geojsonSources.find(s => s.sourceId === layer.source)
            if (source && !map.getSource(source.sourceId)) {
              map.addSource(source.sourceId, source.source.obj)
            }
          }
        }
      }

      // レイヤー追加
      const PREFIX = 'oh-vector-osm-bright-labels-only-'
      const ICON_ONLY_ID = 'oh-vector-osm-bright-labels-only-poi-level-1-1'
      layer.layers.forEach(layer0 => {
        if (!map.getLayer(layer0.id)) {
          if (!layer0.id.includes(PREFIX)) {
            try {
              addLayerWithFadeIn(map, layer0, undefined, layer.opacity)
            } catch (e) { return }
          } else {
            try {
              readdByIdPreservingLast(map, originalStyle, layer0.id, null, ICON_ONLY_ID)
            } catch (e) { return }
          }
        }
        if (!map.getLayer(layer0.id)) return

        // opacity + visibility設定
        setLayerOpacity(map, layer0, layer.opacity)
        setLayerVisibility(map, layer0.id, layer.visibility)
      })

      // ext処理
      if (!this.isDragging && layer.ext) {
        if (layer.ext.values) {
          layer.ext.values.forEach((v, i) => {
            this.$store.commit('updateParam', {
              name: layer.ext.name,
              mapName: this.mapName,
              value: String(v),
              order: i
            })
          })
        }
        const shouldAutoOpen = layer.ext.autoOpenDialog !== false && !this.is4ScreenMode
        if (shouldAutoOpen) {
          this.infoOpen(layer, false, true)
          if (this.$store.state.suppressExtAutoOpen) {
            const windowId = 'dialog-ext-' + this.mapName + '-' + layer.id
            this.$nextTick(() => {
              this.$store.commit('setFloatingVisible', { id: windowId, visible: false })
            })
          }
        }
      }
      if (layer.info && !this.is4ScreenMode) this.infoOpen(layer, false, true)
    },
    // ---- 差分レイヤーシステム ここまで ----
    async addLayers() {
      // addLayersRequestedカウンターで実行判定（旧watchFlg）
      if(!consumeAddLayersRequest(this.$store, this.mapName) && !this.isDragging) {
        return
      }
      // ------------------------------------------------------------------
      const map = this.$store.state[this.mapName]
      if (!map || typeof map.getStyle !== 'function' || !map.getStyle()) {
        // map未準備: イベントで待機（ポーリングしない）
        requestAddLayers(this.$store, this.mapName)
        if (!this.waitingForMap) {
          this.waitingForMap = true
          const checkMap = () => {
            const m = this.$store.state[this.mapName]
            if (m && typeof m.getStyle === 'function' && m.getStyle()) {
              this.waitingForMap = false
              this.scheduleAddLayers()
            } else {
              setTimeout(checkMap, 200)
            }
          }
          setTimeout(checkMap, 200)
        }
        return
      }
      if (typeof map.isStyleLoaded === 'function' && !map.isStyleLoaded() && this.enginePhase !== 'ready') {
        // style未ロード（初回のみ）: ポーリングで待機
        // enginePhase === 'ready' の場合はスキップ（ラスターソースのタイルロード中にブロックされるのを防ぐ）
        // 注意: map.once('idle') は使わない。マップが既にidle状態（空スタイル）の場合、
        //        次のidleイベントが発火せずデッドロックするため。
        requestAddLayers(this.$store, this.mapName)
        if (!this.waitingForStyle) {
          this.waitingForStyle = true
          const checkStyleLoaded = () => {
            if (map.isStyleLoaded()) {
              this.waitingForStyle = false
              this.scheduleAddLayers()
            } else {
              setTimeout(checkStyleLoaded, 200)
            }
          }
          setTimeout(checkStyleLoaded, 200)
        }
        return
      }
      // ---- 差分比較による早期return（ready状態のみ） ----
      const selectedLayersForDiff = this.s_selectedLayers[this.mapName] || []
      const currentSnapshot = computeLayerSnapshot(selectedLayersForDiff)
      const prevSnapshot = this.prevSnapshot[this.mapName]

      if (prevSnapshot && this.enginePhase === 'ready') {
        const diff = diffLayerSnapshots(prevSnapshot, currentSnapshot)

        if (diff.type === 'none') {
          // ext.values変更のみ → レイヤー操作不要
          return
        }

        if (diff.type === 'property_only') {
          // opacity/visibilityのみ変更 → 該当レイヤーだけ更新
          try {
            this.applyPropertyChanges(map, diff.changes, selectedLayersForDiff)
            this.prevSnapshot[this.mapName] = currentSnapshot
            return
          } catch (e) {
            console.warn('⚠️差分property適用失敗、フルリビルドへ:', e)
            // フォールバック: 従来のフルリビルドパスへ
          }
        }

        if (diff.type === 'structural') {
          try {
            this.applyStructuralChanges(map, diff, selectedLayersForDiff)
            this.reorderLayers()
            forseMoveLayer(map)
            this.registerChibanClickHandlers(map)
            this.prevSnapshot[this.mapName] = currentSnapshot
            this.prevFullLayers[this.mapName] = selectedLayersForDiff
            return
          } catch (e) {
            console.warn('⚠️差分structural適用失敗、フルリビルドへ:', e)
            // フォールバック: 従来のフルリビルドパスへ
          }
        }
      }
      // スナップショット保存（フルリビルドパス用）
      this.prevSnapshot[this.mapName] = currentSnapshot
      this.prevFullLayers[this.mapName] = selectedLayersForDiff

      // まずレイヤーを削除（従来のフルリビルドパス）-------------------------
      // フルリビルドでレイヤーが消えるのでクリックハンドラ登録済みセットもクリア
      registeredLayers.clear()
      const layers = map.getStyle().layers
      const failedRemovalIds = new Set()
      if (layers) {
        for (let i = layers.length - 1; i >= 0; i--) {
          const layerId = layers[i].id
          // oh-ai-share-* レイヤーは shareFeatures 専用なので削除しない
          if (layerId.slice(0,2) === 'oh' && !layerId.startsWith('oh-ai-share')) {
            try {
              map.removeLayer(layerId)
            } catch (error) {
              failedRemovalIds.add(layerId)
            }
          }
        }
      }
      // blendソースも削除（レイヤー削除後でないとremoveSourceが失敗する）
      const styleAfterLayerRemoval = map.getStyle()
      if (styleAfterLayerRemoval && styleAfterLayerRemoval.sources) {
        Object.keys(styleAfterLayerRemoval.sources).forEach(sourceId => {
          if (sourceId.startsWith('oh-blend-')) {
            try { map.removeSource(sourceId) } catch (e) { /* ignore */ }
          }
        })
      }

      function isObject(value) {
        return value !== null && typeof value === 'object' && !Array.isArray(value);
      }

      const selectedLayersForMap = this.s_selectedLayers[this.mapName] || []
      const blendInSelected = selectedLayersForMap.filter(l => l.id && l.id.includes('blend')).map(l => l.id)
      const failedLayerLabels = new Set()
      const markLayerFailure = (layer) => {
        const cleanLabel = layer.label ? layer.label.replace(/<[^>]*>/g, '').trim() : layer.id
        failedLayerLabels.add(cleanLabel || layer.id)
      }
      for (let i = selectedLayersForMap.length - 1; i >= 0 ; i--){
        const layer = selectedLayersForMap[i]
        if (layer.layers) {
          if (layer.sources) {
            layer.sources.forEach(source => {
              if (!map.getSource(source.id)) {
                // 動的ソースURL対応（getTiles関数がある場合）
                const sourceObj = source.getTiles
                  ? { ...source.obj, tiles: source.getTiles() }
                  : source.obj
                map.addSource(source.id, sourceObj)
              }
            })
          }
          if (layer.source) {
            if (isObject(layer.source)) {
              if (!map.getSource(layer.source.id)) {
                // 動的ソースURL対応（getTiles関数がある場合）
                const sourceObj = layer.source.getTiles
                  ? { ...layer.source.obj, tiles: layer.source.getTiles() }
                  : layer.source.obj
                map.addSource(layer.source.id, sourceObj)
              }
            } else {
              if (!map.getSource(layer.source)) {
                const source = this.$store.state.geojsonSources.find(source => {
                  return source.sourceId === layer.source
                })
                if (source) {
                  if (!map.getSource(source.sourceId)) {
                    map.addSource(source.sourceId, source.source.obj)
                  }
                }
              }
            }
          }

          let flyFlg = true
          layer.layers.forEach(layer0 => {
              if (!map.getLayer(layer0.id)) {
                const PREFIX = 'oh-vector-osm-bright-labels-only-'
                const ICON_ONLY_ID = 'oh-vector-osm-bright-labels-only-poi-level-1-1'
                if (!layer0.id.includes(PREFIX)) {
                  try {
                    // 地番図ラインレイヤーの場合、addLayer前にpaint['line-width']を変更
                    // これにより addLayer → setPaintProperty のフラッシュを防止
                    if ((layer0.id.includes('oh-chibanzu-line') || (layer0.id.includes('oh-chibanL-') && layer0.id.includes('line'))) && layer.ext && layer.ext.values && layer.ext.values[3]) {
                      try {
                        const chibanWidhsString = layer.ext.values[3]
                        const chibanWidhs = JSON.parse(chibanWidhsString)
                        const found = chibanWidhs.find(v => v.layerId === 'oh-chibanzu-all2')
                        if (found && found.width != null && found.width) {
                          // layer0.paintを直接変更して、最初から正しい線幅でレイヤーを追加
                          if (layer0.paint) {
                            layer0.paint['line-width'] = Number(found.width)
                          }
                        }
                      } catch (e) {
                        // パースエラー時は無視（デフォルト線幅のまま）
                      }
                    }
                    // 登記所地図2025: addLayer前にpaintを事前適用（フラッシュ防止）
                    // ext.values: [0]=text, [1]=lineColor, [2]=circleColor, [3]=lineWidth
                    if (layer0.id.startsWith('oh-homusyo-2025-') && layer.ext && layer.ext.values) {
                      const extValues = layer.ext.values
                      if (layer0.id === 'oh-homusyo-2025-line' && layer0.paint) {
                        // 線色の事前適用
                        if (extValues[1]) layer0.paint['line-color'] = extValues[1]
                        // 線幅の事前適用（nullは標準スタイル＝事前適用不要）
                        const lineWidth = extValues[3] != null ? Number(extValues[3]) : null
                        if (lineWidth) layer0.paint['line-width'] = lineWidth
                      }
                      if (layer0.id === 'oh-homusyo-2025-label' && layer0.paint) {
                        // ラベル色の事前適用
                        if (extValues[1]) layer0.paint['text-color'] = extValues[1]
                      }
                      if (layer0.id === 'oh-homusyo-2025-vertex' && layer0.paint) {
                        // 頂点色の事前適用
                        if (extValues[2]) layer0.paint['circle-color'] = extValues[2]
                      }
                      // dissolvedレイヤーの塗り色の事前適用
                      if ((layer0.id === 'oh-homusyo-2025-polygon-dissolved-all' || layer0.id === 'oh-homusyo-2025-polygon-dissolved') && layer0.paint) {
                        if (extValues[1]) {
                          const toRGBA = (color, alpha) => {
                            const map = { red: [255,0,0], black: [0,0,0], blue: [0,0,255], green: [0,128,0], orange: [255,165,0] }
                            const rgb = map[color?.toLowerCase?.()]
                            return rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})` : color
                          }
                          layer0.paint['fill-color'] = toRGBA(extValues[1], 0.4)
                        }
                      }
                    }
                    // GSI単写真レイヤーの場合、addLayer前にフィルタを設定
                    // これにより addLayer → setFilter の視覚的フラッシュを防止
                    // layer.ext.values: [機関リスト(カンマ区切り), 開始年, 終了年]
                    // 注意: layer0を直接変更するとVueリアクティビティで無限ループになるため、コピーを使用
                    let layerToAdd = layer0
                    if (layer0.id === 'oh-gsi-pp-full' && layer.ext && layer.ext.values && layer.ext.values.length >= 3) {
                      const agenciesStr = layer.ext.values[0]
                      const yearMin = parseInt(layer.ext.values[1], 10)
                      const yearMax = parseInt(layer.ext.values[2], 10)

                      // フィルタ条件を構築
                      const conditions = []
                      let hideAll = false

                      // 撮影計画機関フィルタ（常に適用 - データに他の機関も含まれる可能性があるため）
                      if (agenciesStr) {
                        const agencies = agenciesStr.split(',').filter(a =>
                          ['国土地理院', '米軍', '陸軍'].includes(a)
                        )
                        if (agencies.length > 0) {
                          conditions.push(['in', ['get', '撮影計画機関'], ['literal', agencies]])
                        } else {
                          // 全部オフの場合は何も表示しない
                          hideAll = true
                        }
                      }

                      // 撮影年フィルタ
                      if (!hideAll && !isNaN(yearMin) && !isNaN(yearMax) && (yearMin !== 1936 || yearMax !== 2025)) {
                        const yearExpr = ['to-number', ['slice', ['get', '撮影年月日'], 0, 4]]
                        conditions.push([
                          'all',
                          ['>=', yearExpr, yearMin],
                          ['<=', yearExpr, yearMax],
                          ['!=', yearExpr, 1111] // 無効値を除外
                        ])
                      }

                      // フィルタを設定（元のオブジェクトを変更せず、コピーを作成）
                      if (hideAll) {
                        layerToAdd = { ...layer0, filter: false }
                      } else if (conditions.length > 0) {
                        const filter = conditions.length === 1 ? conditions[0] : ['all', ...conditions]
                        layerToAdd = { ...layer0, filter }
                      }
                    }
                    addLayerWithFadeIn(map, layerToAdd, undefined, layer.opacity)
                  } catch (error) {
                    markLayerFailure(layer)
                    return
                  }
                } else {
                  try {
                    readdByIdPreservingLast(map, originalStyle, layer0.id, null, ICON_ONLY_ID)
                  } catch (error) {
                    markLayerFailure(layer)
                    return
                  }
                }

                if (layer0.id.includes('oh-pmtiles-') && layer0.id.endsWith('-point-layer')) {
                  const id = layer0.id.split('-')[2]

                axios.get('/oh3/php/userPmtiles0SelectById.php', {
                  params: { id: id }
                }).then(function (response) {
                  const labelLaiyrId = `oh-pmtiles-${id}-label-layer`
                  const polygonLaiyrId = `oh-pmtiles-${id}-layer`
                  const vertexLaiyrId = `oh-pmtiles-${id}-vertex-layer`
                  const polygonLineLaiyrId = `oh-pmtiles-${id}-line-layer`

                  if (JSON.parse(response.data[0].style)) {
                    const circle = JSON.parse(response.data[0].style).circle
                    const symbol = JSON.parse(response.data[0].style).symbol
                    const polygon = JSON.parse(response.data[0].style).polygon

                    Object.entries(circle).forEach(([prop, val]) => {
                      if (val) {
                        map.setPaintProperty(layer0.id, prop, val)
                      }
                    })
                    if (symbol) {
                      map.setLayoutProperty(
                          labelLaiyrId,
                          'text-field',
                          ['get', symbol['text-field']]
                      )
                      map.setLayoutProperty(
                          labelLaiyrId,
                          'text-size',
                          symbol['text-size']
                      )
                      map.setPaintProperty(
                          labelLaiyrId,
                          'text-color',
                          symbol['text-color']
                      )
                    }
                    if (polygon) {
                      map.setPaintProperty(
                          polygonLaiyrId,
                          'fill-color',
                          polygon['fill-color']
                      )
                      map.setPaintProperty(
                          polygonLineLaiyrId,
                          'line-color',
                          polygon['line-color']
                      )
                      map.setPaintProperty(
                          polygonLineLaiyrId,
                          'line-width',
                          polygon['line-width']
                      )
                      // alert(polygon['circle-radius'])
                      // if (map.getLayer('vertexLaiyrId')) {
                      //   map.setPaintProperty(
                      //       vertexLaiyrId,
                      //       'circle-radius', [
                      //         'interpolate',
                      //         ['linear'],
                      //         ['zoom'],
                      //         15, 0,
                      //         18, polygon['circle-radius']
                      //       ]);
                      //   map.setPaintProperty(
                      //       vertexLaiyrId,
                      //       'circle-color',
                      //       polygon['circle-color']
                      //   )
                      // }
                    }
                  }
                })

                }
                if (layer0.position) {
                  if (flyFlg && this.nodeClicked) {
                    setTimeout(() => {
                      map.flyTo({
                        center: Array.from(layer0.position), // フライ先の座標（経度, 緯度）
                        zoom: layer0.z ? layer0.z : this.flyZoom,                   // ズームレベル（任意）
                        speed: 1.2,                 // アニメーション速度（オプション）
                        curve: 1.42,                // アニメーションの曲線効果（オプション）
                        essential: true            // ユーザーがアニメーションを無効化していても実行
                      });
                      flyFlg = false
                      this.nodeClicked = false
                    },1000)
                  }
                }
            }
            if (!map.getLayer(layer0.id)) {
              markLayerFailure(layer)
              return
            }
            // opacity + visibility設定
            try {
              setLayerOpacity(map, layer0, layer.opacity)
              setLayerVisibility(map, layer0.id, layer.visibility)
            } catch (error) {
              markLayerFailure(layer)
            }
          })
          if(!this.isDragging) {
            // RRIM直接マウント（ext経由を廃止）
            if (layer.rrim) {
              if (!this.$store.state.rrimItems[this.mapName].some(item => item.id === layer.id)) {
                this.$store.state.rrimItems[this.mapName].push(layer)
              }
            }
            if (layer.ext) {
                if (layer.ext.values) {
                  layer.ext.values.forEach((v,i) => {

                    // if (i === 1) {
                    //   alert(layer.id + '///' + String(v))
                    // }

                    this.$store.commit('updateParam', {
                      name: layer.ext.name,
                      mapName: this.mapName,
                      value: String(v),
                      order: i
                    })
                  })
                }

              // autoOpenDialogがfalseの場合、または4画面モード時はダイアログを開かない
              const shouldAutoOpen = layer.ext.autoOpenDialog !== false && !this.is4ScreenMode

              if (shouldAutoOpen) {
                this.infoOpen(layer, false, true)
                // 起動時レイヤー復元中はextをマウントするが非表示にする
                if (this.$store.state.suppressExtAutoOpen) {
                  const windowId = 'dialog-ext-' + this.mapName + '-' + layer.id
                  this.$nextTick(() => {
                    this.$store.commit('setFloatingVisible', { id: windowId, visible: false })
                  })
                }
              }
            }
          }
          // 4画面モード時はinfoダイアログも開かない
          if (layer.info && !this.is4ScreenMode) this.infoOpen(layer,false, true)
        }
        if (this.enginePhase === 'init') zenkokuChibanzuAddLayer(map,map.getZoom())
      }
      // 起動時レイヤー復元中のext抑制フラグはApp.vue側でクリアする（複数マップ対応）
      // SIMA/KML/GeoJSON/DXF/GPX復元（init以外で実行）
      if (this.enginePhase !== 'init') {
        restoreUserFiles(map, this.$store)
      }
      // アップロードベクター/画像復元（restoringフェーズのみ）
      if (this.enginePhase === 'restoring') {
        restoreUploadedAssets(map, this.$store)
      }

      applyHighlights(map)
      // フェーズ遷移: init→restoring→ready
      this.counter++
      if (this.enginePhase === 'init') {
        this.enginePhase = 'restoring'
      } else if (this.enginePhase === 'restoring') {
        this.enginePhase = 'ready'
      }

      if (map.getSource('click-points-source')) {
        map.removeLayer('click-points-layer')
        map.removeSource('click-points-source')
      }

      if (this.$store.state.clickGeojsonText) {
        clickPointSource.obj.data = JSON.parse(this.$store.state.clickGeojsonText)
      }

      map.addSource('click-points-source', clickPointSource.obj)
      map.addLayer(clickPointLayer)
      if (this.$store.state.isClickPointsLayer) {
        map.setLayoutProperty("click-points-layer", "visibility", 'visible');
      } else {
        map.setLayoutProperty("click-points-layer", "visibility", 'none');
      }

      const fileExtension = 'kml'
      if (map.getLayer(fileExtension + '-layer')) {
        map.moveLayer(fileExtension + '-polygon-layer')
        map.moveLayer(fileExtension + '-layer')
        map.moveLayer(fileExtension + '-line-layer')
        map.moveLayer(fileExtension + '-point-layer')
        // map.moveLayer(fileExtension + '-polygon-points')
      }
      // ---------------------------------------------------------------------------------
      const geojsonText = this.$store.state.clickCircleGeojsonText
      if (geojsonText && typeof geojsonText === 'string' && geojsonText.trim() && geojsonText.trim().startsWith('{')) {
        try {
          clickCircleSource.obj.data = JSON.parse(geojsonText)
        }catch (e) {
          // 初期化時に空データの場合があるためエラーは無視
        }
      }

      // 安全にレイヤーとソースを削除（レイヤー削除→ソース削除の順）
      const safeRemoveLayer = (layerId) => {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
      };
      const safeRemoveSource = (sourceId) => {
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      };

      if (map.getSource('click-circle-source')) {
        // click-circle関連
        safeRemoveLayer('click-circle-layer')
        safeRemoveLayer('click-circle-layer-ex')
        safeRemoveLayer('click-circle-polygon-line-layer')
        safeRemoveLayer('click-circle-polygon-symbol-layer')
        safeRemoveLayer('click-circle-polygon-symbol-area-layer')
        safeRemoveLayer('click-circle-polygon-population-layer')
        safeRemoveLayer('click-circle-symbol-layer')
        safeRemoveLayer('click-circle-line-layer')
        safeRemoveLayer('click-circle-line-dashed-layer')
        safeRemoveLayer('click-circle-line-dotted-layer')
        safeRemoveLayer('click-circle-keiko-line-layer')
        safeRemoveLayer('click-circle-label-layer')
        safeRemoveLayer('click-circle-center-label-layer')
        safeRemoveLayer('click-circle-memo-mark-layer')
        safeRemoveSource('click-circle-source')

        // end-point関連
        safeRemoveLayer('arrows-endpoint-label-layer')
        safeRemoveLayer('arrows-endpoint-layer')
        safeRemoveSource('end-point-source')

        // guide-line関連（レイヤーを先に削除）
        safeRemoveLayer('guide-polygon-fill-layer')
        safeRemoveLayer('guide-line-layer')
        safeRemoveLayer('guide-line-vertex-layer')
        safeRemoveSource('guide-line-source')

        // vertex関連
        safeRemoveLayer('vertex-layer')
        safeRemoveSource('vertex-source')

        // midpoint関連
        safeRemoveLayer('midpoint-layer')
        safeRemoveSource('midpoint-source')

        // segment-label関連
        safeRemoveLayer('segment-label-layer')
        safeRemoveSource('segment-label-source')

        // polygon-edge-label関連
        safeRemoveLayer('polygon-edge-label-layer')
        safeRemoveSource('polygon-edge-label-source')

        // freehand-preview関連
        safeRemoveLayer('freehand-preview-layer')
        safeRemoveSource('freehand-preview-source')

        // elevation関連
        safeRemoveLayer('elevation-layer')
        safeRemoveSource('elevation-source')

        // drag-handles関連
        safeRemoveLayer('drag-handles-layer')
        safeRemoveSource('drag-handles-source')

        // draw-selection関連
        safeRemoveLayer('draw-selection-polygon-fill-layer')
        safeRemoveLayer('draw-selection-polygon-line-layer')
        safeRemoveLayer('draw-selection-line-layer')
        safeRemoveLayer('draw-selection-point-layer')
        safeRemoveSource('draw-selection-source')
      }

      // 安全にソースとレイヤーを追加
      const safeAddSource = (sourceId, sourceObj) => {
        if (!map.getSource(sourceId)) map.addSource(sourceId, sourceObj);
      };
      const safeAddLayer = (layerObj) => {
        if (!map.getLayer(layerObj.id)) {
          try {
            map.addLayer(layerObj);
          } catch (error) {
            console.error(`Failed to add layer ${layerObj.id}:`, error);
            console.error('Layer definition:', layerObj);
          }
        }
      };

      // SDFアイコンが存在しない場合は追加（リロード時のタイミング対策、高解像度でスムーズに）
      if (!map.hasImage('arrow-sdf')) {
        const arrowCanvas = document.createElement('canvas')
        const arrowSize = 64 // 高解像度でスムーズな縁に
        arrowCanvas.width = arrowSize
        arrowCanvas.height = arrowSize
        const ctx = arrowCanvas.getContext('2d', { willReadFrequently: true })
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.fillStyle = '#ffffff'
        ctx.beginPath()
        ctx.moveTo(arrowSize * 0.9, arrowSize * 0.5)
        ctx.lineTo(arrowSize * 0.1, arrowSize * 0.15)
        ctx.lineTo(arrowSize * 0.1, arrowSize * 0.85)
        ctx.closePath()
        ctx.fill()
        const arrowImageData = ctx.getImageData(0, 0, arrowSize, arrowSize)
        map.addImage('arrow-sdf', arrowImageData, { sdf: true })
      }

      safeAddSource('end-point-source', endPointSouce.obj)
      safeAddLayer(arrowsEndpointLayer)
      safeAddLayer(arrowsEndpointLabelLayer)

      safeAddSource('click-circle-source', clickCircleSource.obj)
      safeAddLayer(clickCircleLayer)
      safeAddLayer(clickCircleLayerEx)
      safeAddLayer(clickCirclePolygonLineLayer)
      safeAddLayer(clickCircPolygonSymbolLayer)
      safeAddLayer(clickCircPolygonSymbolAreaLayer)
      safeAddLayer(clickCircPolygonPopulationLayer)
      safeAddLayer(clickCircSymbolLayer)
      safeAddLayer(clickCircleLineLayer)
      safeAddLayer(clickCircleLineDashedLayer)
      safeAddLayer(clickCircleLineDottedLayer)
      safeAddLayer(clickCircleKeikoLineLayer)
      if (map.getLayer(clickCircleKeikoLineLayer.id)) {
        map.setPaintProperty(clickCircleKeikoLineLayer.id, 'line-opacity', 0.3)
      }
      safeAddLayer(clickCircleMemoMarkLayer)
      safeAddLayer(clickCircleLabelLayer)

      // ドロー選択表示用レイヤー（選択された図形を強調表示）
      safeAddSource('draw-selection-source', drawSelectionSource.obj)
      safeAddLayer(drawSelectionPolygonFillLayer)
      safeAddLayer(drawSelectionPolygonLineLayer)
      safeAddLayer(drawSelectionLineLayer)
      safeAddLayer(drawSelectionPointLayer)

      safeAddSource('guide-line-source', guideLineSource.obj)
      safeAddLayer(guideLineLayer)
      safeAddLayer(guideLineVertexLayer)

      safeAddSource('vertex-source', vertexSource.obj)
      safeAddLayer(vertexLayer)

      safeAddSource('midpoint-source', midpointSource.obj)
      safeAddLayer(midpointLayer)

      safeAddSource('segment-label-source', segmentLabelSource.obj)
      safeAddLayer(segmentLabeleLayer)

      safeAddSource('polygon-edge-label-source', polygonEdgeLabelSource.obj)
      safeAddLayer(polygonEdgeLabelLayer)

      safeAddSource('freehand-preview-source', freehandPreviewSource.obj)
      safeAddLayer(freehandPreviewLayer)

      safeAddSource('elevation-source', elevationSource.obj)
      safeAddLayer(elevationLayer)

      safeAddSource('drag-handles-source', dragHandlesSource.obj)
      safeAddLayer(dragHandleslayer)

      // 円の中心ラベルレイヤーを最後に追加（ポリゴンの上に表示）
      safeAddLayer(clickCircleCenterLabelLayer)

      //↓なぜかこうしないとレイヤー順が正しくならない。
      if (map.getLayer('click-circle-polygon-line-layer')) {
        map.moveLayer('click-circle-polygon-line-layer');
      }
      if (map.getLayer('click-circle-label-layer')) {
        map.moveLayer('click-circle-label-layer');
      }
      if (map.getLayer('click-circle-center-label-layer')) {
        map.moveLayer('click-circle-center-label-layer');
      }
      // map.moveLayer('clickCircleKeikoLineLayer.id');

      this.$store.state.drawFire = !this.$store.state.drawFire

      const geojsonTextForLabels = this.$store.state.clickCircleGeojsonText
      if (geojsonTextForLabels && typeof geojsonTextForLabels === 'string' && geojsonTextForLabels.trim() && geojsonTextForLabels.trim().startsWith('{')) {
        try {
          const geojson = JSON.parse(geojsonTextForLabels)
          generateSegmentLabelGeoJSON(geojson)
          generateStartEndPointsFromGeoJSON(geojson)
          updatePolygonEdgeLabels(geojson)
        }catch (e) {
          // 初期化時に空データの場合があるためエラーは無視
        }
      }

      this.registerChibanClickHandlers(map)

      if (map === this.$store.state.map01) {
        setTimeout(() => {
          const targetLayers = map.getStyle().layers
              .filter(layer => layer.id.startsWith('oh-sima-') && !layer.id.endsWith('-polygon-label-layer') && !registeredLayers.has(layer.id))
              .map(layer => layer.id);
          targetLayers.forEach(layer => {
            registeredLayers.add(layer)
            map.on('click', layer, (e) => {
              if (e.features && e.features.length > 0) {
                // 最小面積のポリゴンを選ぶ
                let smallestFeature = e.features[0];
                let minArea = turf.area(e.features[0]);
                for (let feature of e.features) {
                  const area = turf.area(feature);
                  if (area < minArea) {
                    smallestFeature = feature;
                    minArea = area;
                  }
                }
                const targetId = `${smallestFeature.properties['id']}`;
                if (this.$store.state.highlightedSimas.has(targetId)) {
                  this.$store.state.highlightedSimas.delete(targetId);
                  store.commit('setRightDrawer', false);
                } else {
                  this.$store.state.highlightedSimas.add(targetId);
                }
                highlightSpecificFeaturesSima(map, layer);
              }
            });
          });
        },0)
      }

      // DXFレイヤーのクリック処理（SIMAと同じ構造）
      if (map === this.$store.state.map01) {
        setTimeout(() => {
          const targetDxfLayers = map.getStyle().layers
              .filter(layer => layer.id.startsWith('oh-dxf-') && !layer.id.endsWith('-polygon-label-layer') && !layer.id.endsWith('-point-label-layer') && !layer.id.endsWith('-vertex-layer') && !registeredLayers.has(layer.id))
              .map(layer => layer.id);
          targetDxfLayers.forEach(layer => {
            registeredLayers.add(layer)
            map.on('click', layer, (e) => {
              if (e.features && e.features.length > 0) {
                // 最小面積のポリゴンを選ぶ（ラインやポイントの場合は最初の要素）
                let smallestFeature = e.features[0];
                if (layer.includes('-polygon-layer') && e.features.length > 1) {
                  let minArea = turf.area(e.features[0]);
                  for (let feature of e.features) {
                    const area = turf.area(feature);
                    if (area < minArea) {
                      smallestFeature = feature;
                      minArea = area;
                    }
                  }
                }
                const targetHandle = `${smallestFeature.properties['handle']}`;
                if (this.$store.state.highlightedDxfs.has(targetHandle)) {
                  this.$store.state.highlightedDxfs.delete(targetHandle);
                  store.commit('setRightDrawer', false);
                } else {
                  this.$store.state.highlightedDxfs.add(targetHandle);
                }
                // すべてのDXFレイヤーを更新
                highlightSpecificFeaturesDxf(map, layer);
              }
            });
          });
        },0)
      }

      map.getStyle().layers.forEach(layer => {
        if (layer.id.includes('-sima-')) {
          highlightSpecificFeaturesSima(map,layer.id)
          // if (this.$store.state.simaTextForUser) {
          //   map.setPaintProperty(layer.id, 'fill-opacity', JSON.parse(this.$store.state.simaTextForUser).opacity)
          // } else {
          //   map.setPaintProperty(layer.id, 'fill-opacity', 0)
          // }
        }
        if (layer.id.includes('oh-dxf-')) {
          highlightSpecificFeaturesDxf(map,layer.id)
        }
        if (layer.id.includes('oh-chiban-') || layer.id.includes('oh-chibanzu-')) {
          try {
            highlightSpecificFeaturesCity(map,layer.id)
          }catch (e) {
          }
        }
      })
      //
      // if (map.getLayer('td-linestring')) {
      //   map.moveLayer( 'td-linestring')
      //   map.moveLayer( 'td-polygon')
      //   map.moveLayer( 'td-polygon-outline')
      //   map.moveLayer( 'td-point')
      //   map.moveLayer( 'terradraw-measure-polygon-label')
      //   map.moveLayer( 'terradraw-measure-line-label')
      //   map.moveLayer( 'terradraw-measure-line-node')
      //   updateMeasureUnit('m')
      // }
      highlightSpecificFeatures2025(map,'oh-homusyo-2025-polygon');
      // zenkokuChibanzuAddLayer(map,map.getZoom())
      //
      // map.setLayoutProperty('YOUR_LAYER_ID', 'icon-image', [
      //   'coalesce',
      //   ['image', 'atm_11'],
      //   ['image', 'bank_11']
      // ]);

      if (failedLayerLabels.size > 0 || failedRemovalIds.size > 0) {
        if (this.addLayersRetryCount < 1) {
          this.addLayersRetryCount += 1
          requestAddLayers(this.$store, this.mapName)
          this.scheduleAddLayers()
        } else {
          this.addLayersRetryCount = 0
          // 起動直後 (復元中) はスナックバー抑止 — 一時的な競合で出ることが多いため
          if (Date.now() < this.layerErrorSuppressUntil) {
            console.warn('[Dialog-layer] レイヤー処理失敗 (復元中なのでスナックバー抑止)', {
              mapName: this.mapName,
              failedLayerLabels: Array.from(failedLayerLabels),
              failedRemovalIds: Array.from(failedRemovalIds),
            })
          } else {
            const messages = []
            if (failedLayerLabels.size > 0) {
              messages.push(`表示: ${Array.from(failedLayerLabels).join(', ')}`)
            }
            if (failedRemovalIds.size > 0) {
              messages.push(`削除: ${failedRemovalIds.size}件`)
            }
            this.$store.dispatch('triggerSnackbarForGroup', {
              message: `レイヤー処理に失敗しました (${messages.join(' / ')})`
            })
          }
        }
      } else {
        this.addLayersRetryCount = 0
      }

      forseMoveLayer(map)

      // 最終状態ログ
      const finalLayers = map.getStyle().layers
      const finalBlend = finalLayers ? finalLayers.filter(l => l.id.includes('blend')).map(l => l.id) : []
      const finalSources = map.getStyle().sources
      const finalBlendSrc = finalSources ? Object.keys(finalSources).filter(s => s.includes('blend')) : []
      if (finalBlend.length > 0 || finalBlendSrc.length > 0) {
      }

      // shareFeatures再表示トリガーを発火（App.vueで監視してshowShareFeaturesOnMapを呼び出す）
      if (this.mapName === 'map01' && this.$store.state.shareFeatures.length > 0) {
        this.$store.state.refreshShareFeaturesFire = !this.$store.state.refreshShareFeaturesFire
      }

      // 3Dレイヤーがあるかチェックして、あれば3Dコントローラーを表示
      const selectedLayers = this.s_selectedLayers[this.mapName] || []
      const has3dLayer = selectedLayers.some(layer => layer.label && layer.label.includes('3D'))
      if (has3dLayer) {
        const terrainBtnDiv = document.getElementById('terrain-btn-div-' + this.mapName)
        if (terrainBtnDiv) terrainBtnDiv.style.display = 'block'
      }

      // レイヤー追加後に微小ズームで再描画を強制（タイルが表示されない問題の対策）
      const currentZoom = map.getZoom()
      map.zoomTo(currentZoom + 0.01, { duration: 0 })
      setTimeout(() => {
        map.zoomTo(currentZoom, { duration: 0 })
      }, 1000)
    },

    // 授業モード同期対応バッジの表示判定（教員のみ）
    showLessonSyncBadge(node) {
      if (!this.$store.state.isTeacher) return false
      return isExtSyncCapable(node?.ext?.name)
    },
    // 3Dジオラマ水面対応レイヤー判定
    isDiorama3DLayer(id) {
      if (!id) return false
      const set = this._diorama3DIds || (this._diorama3DIds = new Set([
        'oh-sea-level', 'oh-hypsometric',
        'oh-reinfolib-kozui', 'oh-reinfolib-tsunami', 'oh-reinfolib-takashio',
        'oh-kozui', 'oh-kk-tsunami-shinsui', 'oh-kk-takashio-shinsui', 'oh-tadankai',
        'oh-kk-usui-shinsui', 'oh-kk-kozui-shinsui',
        'oh-kk-kozui-mesh-10', 'oh-kk-kozui-mesh-20',
        'oh-kozui-saidai', 'oh-keikaku-saidai', 'oh-tsunami',
      ]))
      return set.has(id)
    },
    // お気に入り機能
    isFavorite(layerId) {
      return this.favoriteLayerIds.includes(layerId)
    },
    toggleFavorite(node) {
      if (node && node.id) {
        this.$store.dispatch('toggleFavoriteLayer', node.id)
      }
    },
    toggleFavoritesFilter() {
      this.showOnlyFavorites = !this.showOnlyFavorites
    },
    // お気に入りのドラッグ終了時
    onFavoriteDragEnd() {
      // sortedFavoritesのsetterが呼ばれ、updateFavoriteLayerOrderアクションが実行される
      // ここでは特に追加処理は不要
    },
    // 最近使用したレイヤーの開閉をトラッキング
    trackRecentLayersToggle() {
      this.recentLayersExpanded = !this.recentLayersExpanded
      this.usageTracker?.trackEvent(
        'button',
        this.recentLayersExpanded ? 'expand' : 'collapse',
        'recent-layers-header'
      )
    },
    // 検索フィールド内でのキーボード操作を保護
    handleSearchKeydown(event) {
      // Enterキーが押されて検索文字が空の場合、ツリーを初期状態に戻す
      if (event.key === 'Enter' && !this.searchText.trim()) {
        const collapseAllNodes = (nodes) => {
          return nodes.map(node => {
            const collapsed = { ...node, expanded: false }
            if (Array.isArray(node.nodes) && node.nodes.length > 0) {
              collapsed.nodes = collapseAllNodes(node.nodes)
            }
            return collapsed
          })
        }
        this.layers = collapseAllNodes(this.layers)
        event.stopPropagation()
        return
      }

      // 左右の矢印キー、Home、End、Backspace、Deleteなどの
      // テキスト編集に必要なキーの伝播を止める
      const protectedKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Backspace', 'Delete']
      if (protectedKeys.includes(event.key)) {
        event.stopPropagation()
      }
    },
    // 通常レイヤーツリーのノードの展開/折りたたみイベント
    onNodeExpanded(node) {
      // 必要に応じて処理を追加
    },
    // レイヤーIDからラベルを取得
    getLayerLabelById(layerId) {
      const findLayer = (nodes) => {
        if (!nodes || !Array.isArray(nodes)) return null
        for (const node of nodes) {
          if (node.id === layerId) {
            // HTMLタグを除去
            return node.label ? node.label.replace(/<[^>]*>/g, '').trim() : layerId
          }
          const children = node.children || node.nodes
          if (children && children.length > 0) {
            const found = findLayer(children)
            if (found) return found
          }
        }
        return null
      }
      return findLayer(this.layers) || layerId
    },
    // ファジー検索: Fuse.jsを使用してレイヤーツリーを検索
    fuzzySearchLayers(nodes, searchTerm) {
      if (!nodes || !Array.isArray(nodes) || !searchTerm) return nodes

      // HTMLタグを除去するヘルパー関数
      const stripHtml = (html) => {
        if (!html) return ''
        return html.replace(/<[^>]*>/g, '').trim()
      }

      // すべてのレイヤーをフラット化して検索対象にする
      const flatLayers = []
      const collectLayers = (items, path = []) => {
        for (const item of items) {
          const children = item.children || item.nodes
          if (children && children.length > 0) {
            const cleanLabel = stripHtml(item.label)
            collectLayers(children, [...path, cleanLabel])
          } else if (item.id) {
            // 葉ノード(実際のレイヤー)のみ収集
            const cleanLabel = stripHtml(item.label)
            flatLayers.push({
              ...item,
              cleanLabel,
              searchPath: [...path, cleanLabel].join(' > ')
            })
          }
        }
      }
      collectLayers(nodes)

      // Fuse.jsで検索
      const fuse = new Fuse(flatLayers, {
        keys: [
          { name: 'cleanLabel', weight: 0.7 },
          { name: 'searchPath', weight: 0.3 }
        ],
        threshold: 0.3, // 0.0 = 完全一致, 1.0 = すべてマッチ
        ignoreLocation: true, // 位置に関係なくマッチ
        useExtendedSearch: true,
        includeScore: true,
        minMatchCharLength: 1
      })

      const results = fuse.search(searchTerm)

      // 検索結果をスコア順にソートして返す
      return results.map(result => ({
        ...result.item,
        // 検索ハイライト用のフラグ
        isSearchResult: true,
        searchScore: result.score
      }))
    },
    // 最近使用したレイヤーセクションを追加
    addRecentLayersSection(nodes) {
      if (!this.recentLayerIds || this.recentLayerIds.length === 0) {
        return nodes
      }

      // 最近使用したレイヤーを収集（深いコピーを作成）
      const recentLayers = []
      const findLayerById = (items, targetId) => {
        for (const item of items) {
          if (item.id === targetId) {
            // 深いコピーを作成して返す
            return JSON.parse(JSON.stringify(item))
          }
          const children = item.children || item.nodes
          if (children && children.length > 0) {
            const found = findLayerById(children, targetId)
            if (found) return found
          }
        }
        return null
      }

      for (const layerId of this.recentLayerIds) {
        const layer = findLayerById(nodes, layerId)
        if (layer) {
          recentLayers.push({
            ...layer,
            isRecent: true
          })
        }
      }

      if (recentLayers.length === 0) {
        return nodes
      }

      // 「最近使用したレイヤー」セクションを追加（新しい配列を作成）
      return [
        {
          id: 'recent-layers-section',
          label: '<span style="color: var(--oh3-color-link); font-weight: bold;">📌 最近使用したレイヤー</span>',
          nodes: recentLayers,
          expanded: true
        },
        ...nodes
      ]
    },
    // レイヤー選択時に履歴を更新
    addToRecentLayers(layerId) {
      if (!layerId) return

      // 既存の位置から削除
      const filtered = this.recentLayerIds.filter(id => id !== layerId)

      // 先頭に追加
      this.recentLayerIds = [layerId, ...filtered].slice(0, 5) // 最大5件

      // LocalStorageに保存
      try {
        localStorage.setItem('oh3-recent-layers', JSON.stringify(this.recentLayerIds))
      } catch (error) {
        console.warn('Failed to save recent layers:', error)
      }
    },
    // LocalStorageから最近使用レイヤーを復元
    loadRecentLayers() {
      try {
        const saved = localStorage.getItem('oh3-recent-layers')
        if (saved) {
          this.recentLayerIds = JSON.parse(saved).slice(0, 5)
        }
      } catch (error) {
        console.warn('Failed to load recent layers:', error)
        this.recentLayerIds = []
      }
    },
  },
  created() {
    // スライダーのthrottle版を作成（16ms = 60FPS）
    this.throttledInputSlider = throttle(this.inputSlider, 16, {
      leading: true,  // 最初の呼び出しは即座に実行
      trailing: true  // 最後の呼び出しも確実に実行
    })

    // Initialize usage tracker
    try {
      this.usageTracker = getUsageTracker(undefined, this.$store)
    } catch (error) {
      console.warn('[Dialog-layer] Failed to initialize usage tracker:', error)
    }
  },
  mounted() {
    const savedRecentHeight = localStorage.getItem('oh3-recent-layers-height')
    if (savedRecentHeight) {
      const parsed = Number(savedRecentHeight)
      if (!Number.isNaN(parsed) && parsed > 0) {
        this.recentLayersHeight = parsed
      }
    }
    this.loadRecentLayerPins()
    // マイセット（ユーザーセット）読み込み → チップ配列も構築
    this.loadMysetUserSets().then(() => this.rebuildMysetChips())
    // 初期チップ（システムセットのみ）
    this.rebuildMysetChips()
    // お気に入りと最近使用レイヤーを読み込み
    this.$store.dispatch('loadFavoriteLayersFromStorage')
    this.$store.dispatch('loadRecentLayersFromStorage')
    // ジャンプ位置を読み込み
    this.loadJumpLocations()

    // FloatingWindowを使用するため、drag-handleの設定は不要

    // ----------------------------------------------------------------------------------------------------------------
    // map03/map04はmap01のレイヤー定義を使用
    const layerKey = (this.mapName === 'map03' || this.mapName === 'map04') ? 'map01' : this.mapName;
    this.layers = Layers.layers[layerKey];
    // ----------------------------------------------------

    // FloatingWindowのスロットは遅延レンダリング（<slot v-if="hasBeenVisible">）で、
    // 初回表示まで内部DOM（first/center/second-div）が存在しない。
    // マウント時点で既に可視なら即初期化、そうでなければwatcherが初回可視化で呼ぶ。
    this.$nextTick(() => this.setupLayerSplitDrag())

    // レイヤー名のはみ出しをチェック
    this.checkLabelOverflow()

    // キーボードショートカット: Ctrl+L で検索フィールドにフォーカス
    this.keyboardHandler = (event) => {
      // Ctrl+L または Cmd+L (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 'l') {
        event.preventDefault()
        // 検索フィールドにフォーカス
        const searchField = document.querySelector(`#layer-window-${this.mapName} input[type="text"]`)
        if (searchField) {
          searchField.focus()
          // FloatingWindowが非表示の場合は表示
          const isVisible = this.$store.state.floatingWindows?.['layer-window-' + this.mapName]
          if (!isVisible) {
            this.$store.commit('setFloatingVisible', {
              id: 'layer-window-' + this.mapName,
              visible: true
            })
            // 表示後にフォーカス
            this.$nextTick(() => {
              const field = document.querySelector(`#layer-window-${this.mapName} input[type="text"]`)
              if (field) field.focus()
            })
          }
        }
      }
    }
    document.addEventListener('keydown', this.keyboardHandler)

  },
  updated() {
    // DOM更新後にもチェック（起動時のレイヤー復帰時など）
    this.checkLabelOverflow()
  },
  beforeUnmount() {
    this.cancelScheduledAddLayers()
    // キーボードイベントリスナーを削除
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler)
    }
  },
  watch: {
    // ユーザーIDが変わったとき（ログイン/ログアウト時）にお気に入りとマイセットを再読み込み
    currentUserId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$store.dispatch('loadFavoriteLayersFromStorage')
        this.loadMysetUserSets().then(() => this.rebuildMysetChips())
      }
    },
    // マイセットFWでセットが変更された時にチップを再読み込み
    userSetsVersion() {
      this.loadMysetUserSets().then(() => this.rebuildMysetChips())
    },
    // 検索文字が空になったときにツリーを折りたたむ
    searchText(newVal, oldVal) {
      if (oldVal && !newVal) {
        // 検索文字が消えた時、すべてのノードを折りたたむ
        const collapseAllNodes = (nodes) => {
          return nodes.map(node => {
            const collapsed = { ...node, expanded: false }
            if (Array.isArray(node.nodes) && node.nodes.length > 0) {
              collapsed.nodes = collapseAllNodes(node.nodes)
            }
            return collapsed
          })
        }
        this.layers = collapseAllNodes(this.layers)
      }
    },
    // 最近使用レイヤーの展開状態を監視してキャッシュを更新
    recentLayersExpanded: {
      handler(newVal) {
        if (newVal) {
          // 開いた時にキャッシュを更新
          this.cachedRecentLayers = this.recentLayersForDisplay
        }
      },
      immediate: false,
    },
    s_lngRange: {
      handler: function(){
      },
      deep: true
    },

    //
    // FloatingWindowの表示状態を監視してレイヤー名の2段表示機能を実行
    '$store.state.floatingWindows': {
      handler: function(newVal) {
        const windowId = 'layer-window-' + this.mapName
        if (newVal[windowId]) {
          // FloatingWindowが表示された時にレイヤー名のはみ出しをチェック
          this.$nextTick(() => {
            setTimeout(() => {
              this.checkLabelOverflow()
            }, 350) // App.vueのsetTimeoutより少し後に実行
          })
        }
      },
      deep: true
    },

    s_selectedLayers: {
      handler: function(val){
        const map = this.$store.state[this.mapName]

        if (!map) return
        const bounds = map.getBounds()
        // 南西端と北東端の座標を取得
        const sw = bounds.getSouthWest()
        const ne = bounds.getNorthEast()
        this.$store.state.lngRange = [sw.lng,ne.lng]
        this.$store.state.latRange = [sw.lat,ne.lat]

        this.scheduleAddLayers()

        const st = this.$store.state
        const shouldFit = Object.prototype.hasOwnProperty.call(st, 'isDrawFit')
            ? st.isDrawFit
            : true   // ← プロパティが存在しないときは true
        if (shouldFit) {
          fitClickCircleAll()
          // 削除
          st.isDrawFit = false
        }

        // レイヤー名のはみ出しをチェック
        this.checkLabelOverflow()

        // ジャンプ位置を再取得（debounce）
        clearTimeout(this.jumpLoadTimer)
        this.jumpLoadTimer = setTimeout(() => this.loadJumpLocations(), 500)

        // メモを再取得（debounce）
        clearTimeout(this.memoLoadTimer)
        this.memoLoadTimer = setTimeout(() => this.loadLayerMemos(), 500)
      },
      deep: true
    },

    // extがカスタムレイヤー追加後にreorderLayers()を呼ぶためのトリガー
    '$store.state.reorderLayersFire': {
      handler: function() {
        this.$nextTick(() => {
          this.reorderLayers()
        })
      }
    },
    // レイヤーFWが初回可視化されたらスプリットドラッグを初期化
    // （FloatingWindowのスロットが遅延レンダリングのため、mounted時点では初期化できない）
    isLayerWindowVisible: {
      immediate: true,
      handler(val) {
        if (val) this.$nextTick(() => this.setupLayerSplitDrag())
      },
    },
  },
}
</script>
<style scoped>
/* レイヤー操作ボタンのスタイル */
.layer-action-btn {
  position: relative !important;
  top: -2px !important;
  height: 40px !important;
}

/* AI検索コンテンツ - flexで固定部分とスクロール部分を分離 */
.ai-search-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* 固定部分（検索入力・ボタン） */
.ai-search-fixed {
  flex-shrink: 0;
}

/* スクロール部分（検索結果）*/
.ai-search-scroll-area {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* AI検索結果のスタイル */
.ai-search-results {
  border: 1px solid var(--oh3-color-border-default);
  border-radius: var(--oh3-radius-lg);
}

.ai-result-item {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f0f0f0;
  font-size: 15px;
}

.ai-result-item:last-child {
  border-bottom: none;
}

.ai-result-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.1);
}

/* お気に入り機能のスタイル */
.tree-node-with-favorite {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 4px;
}
.tree-node-label {
  flex: 1;
  min-width: 0;
}
.badge-3d {
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  padding: 1px 3px;
  margin-right: 3px;
  border-radius: 2px;
  background: var(--oh3-color-primary);
  color: #fff;
}
.badge-sync {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1px 3px;
  margin-right: 3px;
  border-radius: 2px;
  background: #e8f5e9;
  color: #2e7d32;
  line-height: 1;
}
.favorite-icon {
  flex-shrink: 0;
  cursor: pointer;
  color: var(--oh3-color-text-tertiary);
  opacity: 0.6;
  transition: all 0.2s ease, transform 0.1s ease;
}
.favorite-icon:hover {
  opacity: 1;
  color: #ffa500;
}
.favorite-icon:active {
  transform: scale(0.75);
}
.favorite-icon.is-favorite {
  color: #ffa500;
  opacity: 1;
}

/* お気に入りドラッグ可能リストのスタイル */
.favorite-draggable-list {
  width: 100%;
}

.favorite-item {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  gap: 8px;
  cursor: pointer;
  background: white;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease, transform 0.1s ease;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: medium;
  color: dimgray;
  position: relative;
  z-index: 0;
}
.favorite-item:active {
  transform: scale(0.97);
}

.favorite-item-row,
.tree-node-row {
  width: 100%;
  position: relative;
  z-index: 0;
  padding-right: 26px;
  overflow: visible;
}

.recent-layer-row {
  width: 100%;
  position: relative;
  z-index: 0;
  padding-right: 0;
  overflow: visible;
}

.layer-click-highlight {
  position: relative;
  z-index: 0;
}

.layer-click-highlight::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(var(--v-theme-primary), 0.14);
  width: 200vw;
  margin-left: calc(100% - 100vw);
  z-index: -1;
}

.favorite-item-row:hover::before,
.recent-layer-row:hover::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background-color: var(--row-hover-background, #e0e0e0);
  width: 200vw;
  margin-left: calc(100% - 100vw);
  z-index: -1;
}

.favorite-item-row.layer-click-highlight::before,
.recent-layer-row.layer-click-highlight::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(var(--v-theme-primary), 0.14);
  width: 200vw;
  margin-left: calc(100% - 100vw);
  z-index: -1;
}


.favorite-drag-handle {
  flex-shrink: 0;
  cursor: grab;
  color: var(--oh3-color-text-tertiary);
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.favorite-drag-handle:hover {
  opacity: 1;
}

.favorite-drag-handle:active {
  cursor: grabbing;
}

.favorite-item-label {
  flex: 1;
  min-width: 0;
}

/* ドラッグ中のスタイル */
.sortable-ghost {
  opacity: 0.4;
  background: #e3f2fd;
}

.sortable-drag {
  opacity: 0.9;
  background: white;
  box-shadow: 0 2px 8px var(--oh3-color-overlay-15);
}

/* ツリーコンテナのラッパー（flex container） */
.tree-container-wrapper {
  flex: 1;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}


/* 最近使用したレイヤーのスクロールエリア */
.recent-layers-section {
  border: 1px solid var(--oh3-color-border-default);
  flex-shrink: 0; /* 縮小しない */
  /* 本番ツリーと同じフォント設定 */
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: var(--oh3-font-size-md); /* ブラウザデフォルトと同じ */
}

.recent-layers-header {
  display: flex;
  align-items: center;
  padding: 5px 10px; /* 本番ツリーと同じパディング */
  cursor: default; /* 本番ツリーと同じ標準カーソル */
  user-select: none;
  font-size: medium; /* 本番ツリーと同じ */
  color: dimgray; /* 本番ツリーと同じ */
  position: relative;
  width: 100%;
  box-sizing: border-box;
  transform-style: preserve-3d; /* 本番ツリーと同じ */
}

/* 本番ツリーと同じホバー効果 */
.recent-layers-header:hover::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background-color: var(--row-hover-background, #e0e0e0); /* 本番ツリーと同じ変数を使用 */
  transform: translate3d(0, 0, -0.1px);
  width: 200vw;
  margin-left: calc(100% - 100vw);
  z-index: -1;
}

.recent-layers-icon-wrapper {
  width: 15px; /* 本番ツリーと同じ幅 */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: dimgray; /* アイコンの色も本番ツリーと同じ */
}

.recent-layers-title {
  flex: 1;
  min-width: 0;
  user-select: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: normal;
}

.recent-layers-count {
  color: gray; /* 本番ツリーのchild-countと同じ */
  margin-left: 6px; /* 本番ツリーと同じ */
  font-size: inherit;
}

/* スクロール可能なリストエリア */
.recent-layers-list-wrapper {
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin; /* Firefox用 */
  /* スクロールイベントを親に伝播させない */
  overscroll-behavior: contain;
}

.recent-layers-resize-handle {
  height: 6px;
  width: 100%;
  background-color: var(--oh3-color-gray-300);
  border-radius: 3px;
  cursor: grab;
  transition: background-color 0.2s ease;
}

.recent-layers-resize-handle:hover {
  background-color: #bdbdbd;
}

.recent-layers-list {
  padding: 0;
  margin: 0;
  display: grid; /* 本番ツリーと同じ display */
  width: 100%;
  gap: 0; /* 行間をなくす */
}

.recent-layer-item {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  padding: 5px 10px 5px 34px;
  cursor: pointer;
  transition: transform 0.1s ease;
  user-select: none;
  font-size: medium; /* 本番ツリーと同じ */
  color: dimgray; /* 本番ツリーと同じ */
  font-weight: normal; /* デフォルト */
  line-height: normal; /* デフォルト */
  transform-style: preserve-3d; /* 本番ツリーと同じ */
  gap: 6px;
  overflow: hidden;
}
.recent-layer-item:active {
  transform: scale(0.97);
}

/* テキストスタイルを本番ツリーと統一 */
.recent-layer-item .tree-node-label {
  user-select: none;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.recent-pin-icon {
  margin-left: auto;
  color: var(--oh3-color-text-tertiary);
  opacity: 0.7;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease, transform 0.1s ease;
}
.recent-pin-icon:active {
  transform: scale(0.75);
}
.recent-pin-icon.is-pinned {
  color: #ffa500;
  opacity: 1;
}

.recent-delete-icon {
  margin-left: 6px;
  color: var(--oh3-color-text-tertiary);
  opacity: 0.7;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease, transform 0.1s ease;
}
.recent-delete-icon:hover {
  color: #e53935;
  opacity: 1;
}
.recent-delete-icon:active {
  transform: scale(0.75);
}

/* Webkit系ブラウザのスクロールバースタイル（Windows Chrome/Edge対応） */
.recent-layers-list-wrapper::-webkit-scrollbar {
  width: 6px; /* より細く */
}

.recent-layers-list-wrapper::-webkit-scrollbar-track {
  background: transparent; /* 透明にしてすっきり */
}

/* デフォルトは透明（非表示） */
.recent-layers-list-wrapper::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 3px;
}

/* ホバー時に表示 */
.recent-layers-list-wrapper:hover::-webkit-scrollbar-thumb {
  background: var(--oh3-color-overlay-20);
}

.recent-layers-list-wrapper:hover::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.4);
}

.container-div {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: auto;
  touch-action: pan-y;
}
.first-div {
  flex: 0 0 auto;
  height: 150px;
  width: 100%;
  background-color: var(--oh3-color-gray-50) !important;
  border: 1px solid var(--oh3-color-border-default);
  border-radius: var(--oh3-radius-lg);
  overflow: auto;
  margin-bottom: var(--oh3-spacing-2);
  overscroll-behavior: none;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
}
.center-div {
  flex: 0 0 6px;
  width: 100%;
  height: 6px;
  background-color: var(--oh3-color-gray-300);
  border-radius: 3px;
  cursor: grab;
  transition: background-color 0.2s ease;
}
.center-div:hover {
  background-color: #bdbdbd;
}
.second-div {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  background-color: white;
  overscroll-behavior: none;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  display: flex;
  flex-direction: column;
}
.search-header {
  flex-shrink: 0;
  height: 60px;
  width: 100%;
  padding: 0 10px;
  padding-bottom: 0;
  margin-bottom: -4px;
  background-color: white;
  box-sizing: border-box;
  display: flex;
  align-items: center;
}
.myset-chips {
  flex-shrink: 0;
  height: 32px;
  padding: 0 10px;
  background-color: white;
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.myset-chips::-webkit-scrollbar {
  display: none;
}
.myset-chips-scroll {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.myset-chip {
  flex-shrink: 0;
  cursor: pointer;
  font-size: var(--oh3-font-size-xs) !important;
}
.myset-chip-add {
  flex-shrink: 0;
}
.myset-chips-scroll .sortable-ghost {
  opacity: 0.4;
}
.myset-chips-scroll .sortable-drag {
  opacity: 0.9;
}
.tree-scroll-container {
  flex: 1;
  width: 100%;
  overflow-y: auto;
  min-height: 0;
  scrollbar-width: thin; /* Firefox用 */
}

/* 親コンテナのスクロールバーもホバー時のみ表示（Windows対応） */
.tree-scroll-container::-webkit-scrollbar {
  width: 6px;
}

.tree-scroll-container::-webkit-scrollbar-track {
  background: transparent;
}

.tree-scroll-container::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 3px;
}

.tree-scroll-container:hover::-webkit-scrollbar-thumb {
  background: var(--oh3-color-overlay-20);
}

.tree-scroll-container:hover::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.4);
}

/* オンボーディング吹き出し */
.onboarding-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.onboarding-tooltip {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}
.drag-item {
  position: relative;
  height: 48px;
  font-size: medium;
  color: var(--oh3-color-text-primary);
  background-color: var(--oh3-color-white);
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.15s ease;
}
.drag-item:hover {
  background-color: #f8f9fa;
}
/* ドロー行（他レイヤーと同様のスタイル） */
.draw-layer-item {
  border: 1px solid var(--oh3-color-border-default);
}
.draw-label {
  font-weight: 500;
}
.handle-div {
  position: absolute;
  left: 0;
  top: 0;
  width: 24px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  border-left: 3px solid #bbb;
  transition: border-color 0.15s ease;
}
.handle-div:active {
  cursor: grabbing;
}
.drag-item:hover .handle-div {
  border-left-color: #888;
}
.handle-icon {
  color: #999 !important;
  transition: color 0.15s ease;
}
.drag-item:hover .handle-icon {
  color: #666 !important;
}
.visible-layer-div {
  position: absolute;
  left: 24px;
  top: 0;
  width: 32px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--oh3-color-text-tertiary);
  transition: color 0.15s ease, transform 0.1s ease;
}
.visible-layer-div:hover {
  color: var(--oh3-color-text-primary);
}
.visible-layer-div:active {
  transform: scale(0.8);
}
.info-div {
  position: absolute;
  left: 56px;
  top: 0;
  width: 32px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--oh3-color-text-tertiary);
  transition: color 0.15s ease, transform 0.1s ease;
}
.info-div:hover {
  color: var(--oh3-color-text-primary);
}
.info-div:active {
  transform: scale(0.8);
}
.delete-div {
  position: absolute;
  left: 88px;
  top: 0;
  width: 32px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #ccc;
  transition: color 0.15s ease, transform 0.1s ease;
}
.delete-div:hover {
  color: #e57373;
}
.delete-div:active {
  transform: scale(0.8);
}
.label-div {
  position: absolute;
  top: 6px;
  left: 124px;
  right: 12px;
  font-size: var(--oh3-font-size-base);
  font-weight: 500;
  color: var(--oh3-color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 18px;
  transition: top 0.15s ease, font-size 0.15s ease, line-height 0.15s ease;

  br {
    display: none;
  }
}

/* レイヤー名が長い場合は2段表示 */
.label-div.label-long {
  top: 2px !important;
  font-size: var(--oh3-font-size-sm) !important;
  line-height: 14px !important;
  white-space: normal !important;
  max-height: 28px;
  text-overflow: clip !important;
}
.jump-dialog-content {
  padding: 10px;
}
.jump-dialog-layer-name {
  font-size: var(--oh3-font-size-lg);
  font-weight: bold;
  color: var(--oh3-color-text-primary);
  margin-bottom: var(--oh3-spacing-4);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.memo-dialog-content {
  padding: 10px;
}
.memo-dialog-layer-name {
  font-size: var(--oh3-font-size-lg);
  font-weight: bold;
  color: var(--oh3-color-text-primary);
  margin-bottom: var(--oh3-spacing-4);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.memo-dialog-text {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: var(--oh3-font-size-sm);
  line-height: 1.6;
  padding: var(--oh3-spacing-2);
  background: rgba(0,0,0,0.05);
  border-radius: var(--oh3-radius-sm);
}
.kebab-menu-list {
  min-width: 160px;
}
.memo-icon {
  color: #FF9800;
}
.layer-right-icons {
  position: absolute;
  right: 4px;
  top: 0;
  height: 50%;
  display: flex;
  align-items: center;
  gap: 2px;
}
.layer-right-icon {
  cursor: pointer;
  padding: 2px;
  border-radius: var(--oh3-radius-sm);
  color: var(--oh3-color-text-tertiary);
  transition: color 0.15s ease, transform 0.1s ease;
  &:hover {
    color: var(--oh3-color-text-primary);
  }
  &:active {
    transform: scale(0.8);
  }
}
.range-div {
  position: absolute;
  bottom: 6px;
  left: 124px;
  right: 12px;
}
.range {
  width: 100%;
  height: 4px;
  border-radius: var(--oh3-radius-sm 4px);
  background: var(--oh3-color-gray-300);
  outline: none;
  -webkit-appearance: none;
}
.range::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 14px;
  height: 14px;
  border-radius: var(--oh3-radius-full);
  background: var(--oh3-color-text-secondary);
  cursor: pointer;
  transition: all 0.15s ease;
  margin-top: -5px;
  box-shadow: 0 1px 3px var(--oh3-color-overlay-15);
}
.range::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: var(--oh3-radius-full);
  background: var(--oh3-color-text-secondary);
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
  box-shadow: 0 1px 3px var(--oh3-color-overlay-15);
}

/* デスクトップ用のホバーエフェクト */
@media (pointer: fine) {
  .range::-webkit-slider-thumb:hover {
    transform: scale(1.15);
    background: var(--oh3-color-gray-800);
  }
  .range::-webkit-slider-thumb:active {
    transform: scale(1.1);
    background: var(--oh3-color-text-primary);
  }
  .range::-moz-range-thumb:hover {
    transform: scale(1.15);
    background: var(--oh3-color-gray-800);
  }
}

/* スマホ・タブレット用のタッチターゲット拡大 */
@media (pointer: coarse) {
  .range::-webkit-slider-thumb {
    width: 44px;
    height: 44px;
    margin-top: -20px;
    border: none;
    box-shadow: none;
    background: radial-gradient(circle 7px at center, var(--oh3-color-text-secondary) 0, var(--oh3-color-text-secondary) 7px, transparent 7.5px);
    background-repeat: no-repeat;
    background-position: center;
    transform: none;
  }
  .range::-moz-range-thumb {
    width: 44px;
    height: 44px;
    border: none;
    box-shadow: none;
    background: radial-gradient(circle 7px at center, var(--oh3-color-text-secondary) 0, var(--oh3-color-text-secondary) 7px, transparent 7.5px);
    background-repeat: no-repeat;
    background-position: center;
    transform: none;
  }
}

.hover:hover {
  color: var(--main-color);
}
.v-icon {
  transition: all 0.2s ease;
}
</style>

<style>
.tree-row-item {
  font-size:medium;
  color:dimgray;
}
.tree-list, .tree-row {
  gap:0!important;
}
/*スライダー*/
input[type=range] {
  height: 26px;
  -webkit-appearance: none;
  margin: 0 0;/*修正*/
  width: 100%;
  background-color: rgba(0,0,0,0);/*修正*/
}
input[type=range]:focus {
  outline: none;
}
input[type=range]::-webkit-slider-runnable-track {
  width: 100%;
  height: 5px;
  cursor: pointer;
  animate: 0.2s;
  box-shadow: 0 0 0 #000000;
  background: #B6B6B6;
  border-radius: var(--oh3-radius-md);
  border: 1px solid #8A8A8A;
}
input[type=range]::-webkit-slider-thumb {
  box-shadow: 1px 1px 1px #828282;
  border: 1px solid #8A8A8A;
  height: 20px;
  width: 20px;
  border-radius: 20px;
  background: #DADADA;
  cursor: grab;
  -webkit-appearance: none;
  margin-top: -10px;
}
@media screen and (max-width:500px) {
  input[type=range]::-webkit-slider-thumb {
    height: 23px;
    width: 30px;
    border-radius: 0;
    margin-top: -12.5px;
  }
  .vue-slider-dot-handle{
    width: 150%!important;
    border-radius:0!important;
  }
}
input[type=range]:focus::-webkit-slider-runnable-track {
  background: #B6B6B6;
}
input[type=range]::-moz-range-track {
  width: 100%;
  height: 5px;
  cursor: pointer;
  animate: 0.2s;
  box-shadow: 0 0 0 #000000;
  background: #B6B6B6;
  border-radius: var(--oh3-radius-md);
  border: 1px solid #8A8A8A;
}
input[type=range]::-moz-range-thumb {
  box-shadow: 1px 1px 1px #828282;
  border: 1px solid #8A8A8A;
  height: 20px;
  width: 20px;
  border-radius: 20px;
  background: #DADADA;
  cursor: grab;
}
input[type=range]::-ms-track {
  width: 100%;
  height: 5px;
  cursor: pointer;
  animate: 0.2s;
  background: transparent;
  border-color: transparent;
  color: transparent;
}
input[type=range]::-ms-fill-lower {
  background: #B6B6B6;
  border: 1px solid #8A8A8A;
  border-radius: var(--oh3-radius-xl);
  box-shadow: 0 0 0 #000000;
}
input[type=range]::-ms-fill-upper {
  background: #B6B6B6;
  border: 1px solid #8A8A8A;
  border-radius: var(--oh3-radius-xl);
  box-shadow: 0 0 0 #000000;
}
input[type=range]::-ms-thumb {
  margin-top: 1px;
  box-shadow: 1px 1px 1px #828282;
  border: 1px solid #8A8A8A;
  height: 20px;
  width: 20px;
  border-radius: 20px;
  background: #DADADA;
  cursor: grab;
}
input[type=range]:focus::-ms-fill-lower {
  background: #B6B6B6;
}
input[type=range]:focus::-ms-fill-upper {
  background: #B6B6B6;
}
/*スライダーここまで*/

/*スマホ用のスタイル*/
@media screen and (max-width: 500px) {
  .drag-item {
    height: 50px!important;
    font-size: var(--oh3-font-size-md)!important;

  }
  .label-div {
    font-size: var(--oh3-font-size-lg)!important;
    top: 6px!important;
  }
  .tree-row-item {
    font-size: large!important;
    line-height: 1.6!important;
    padding: 4px 0!important;
    min-height: 44px!important;
  }
  .tree-row {
    min-height: 44px!important;
  }
  /* 最近使用したレイヤーも本番ツリーと同じレスポンシブ対応 */
  .recent-layer-item {
    font-size: large!important;
    line-height: 1.6!important;
    padding: 2px 0 2px 34px!important; /* 本番ツリーと同じ左右パディング0 */
    min-height: 40px!important;
  }
  .recent-layers-header {
    font-size: large!important;
    line-height: 1.6!important;
    padding: 2px 0!important; /* 本番ツリーと同じ左右パディング0 */
    min-height: 40px!important;
  }
  /* お気に入りドラッグリストもレスポンシブ対応 */
  .favorite-item {
    font-size: large!important;
    line-height: 1.6!important;
    padding: 8px 10px!important;
    min-height: 40px!important;
  }
}
</style>
